/**
 * TypesetterWorkerClient - Main thread interface to Typesetter Web Worker
 * 
 * Provides type-safe API for layout computation with automatic worker management
 * NEW for Phase 2: Includes pagination support with intelligent caching
 */

// ============================================================================
// DEBUG CONTROL: Set to false to disable all debug logging in production
// ============================================================================
const DEBUG_ENABLED = false; // ✅ DISABLED: Rapid typing performance fix

// ============================================================================
// PRODUCTION LOG CONTROL: Set to false to disable Phase3A benchmark logs
// These logs (📊 SpillFrontier, paginateFromCache, etc.) are useful for
// debugging but add overhead in production. Disable for best performance.
// ============================================================================
const PRODUCTION_LOGS_ENABLED = false; // ✅ Set to false for production

import { 
  FixedPoint,
  type ParagraphLayoutInput, 
  type ParagraphSnapshot, 
  type FontData,
  type DocumentLayoutInput,
  type PageWindowResult,
  type PageLayout,
  type PageSetup,
  type Line,
  type CaretPosition
} from '../core/types/Typesetter';
import { FontDownloadService } from '../rendering/fonts/FontDownloadService';
import { WebFontLoader } from '../rendering/fonts/WebFontLoader';
import { fontResolver } from '../services/FontResolver';
// Organized utility imports by category
import { computeDocHash } from './TypesetterClientFunctionClassesObjects/document';
import { convertParagraphRangesToUtf16 } from './TypesetterClientFunctionClassesObjects/glyph';
import { prepareFontsAsTransferable, createTransferableMessagePayload } from './TypesetterClientFunctionClassesObjects/font';
import {
  generatePageCacheKey,
  generatePaginationRangeKey,
  parsePageCacheKey,
  extractPageIndexFromKey,
  findCacheKeysToInvalidate,
  findPageIndicesToInvalidate,
  matchesCacheKeyCriteria,
  matchesDocHash,
  checkCacheForRange,
  evictOldestIfNeeded,
  findMissingPageIndices,
  collectCachedPagesInRange,
  cachePages,
  cachePage,
  cleanupMap,
  filterCacheKeysByDocHash
} from './TypesetterClientFunctionClassesObjects/cache';
import { CacheStatisticsCalculator } from './TypesetterClientFunctionClassesObjects/cache';
import { PageCacheMerger } from './TypesetterClientFunctionClassesObjects/cache';
import { PageHashStorer } from './TypesetterClientFunctionClassesObjects/cache';
import { Phase2CacheClearer } from './TypesetterClientFunctionClassesObjects/cache';
import { CacheOrchestrator, getCacheOrchestrator } from './TypesetterClientFunctionClassesObjects/cache';
import { PageSetupConverter } from '../utils/PageSetupConverter';
import {
  validatePageSetupOrThrow,
  calculatePrefetchRange,
  generatePrefetchRangeKey,
  trackPaginationFault,
  buildPaginationInput,
  updateTotalPageCount,
  filterAndSortPagesByRange,
  extractPageHashes,
  getMinPageIndex,
  processPaginationResult
} from './TypesetterClientFunctionClassesObjects/pagination';
import { SpillFrontierCalculator } from './TypesetterClientFunctionClassesObjects/pagination';
import {
  createFallbackPageWindowResult,
  createCachedPageWindowResult,
  checkCircuitBreaker,
  logCircuitBreakerTrigger,
  shouldAttemptReinitialization,
  logReinitializationFailure,
  checkWasmCrashed,
  isWasmError,
  isSoftHandleableError,
  checkLayoutCancellation,
  markLayoutAsCancelled,
  removeCancelledStatus
} from './TypesetterClientFunctionClassesObjects/error';
import {
  createSnapshotWithIdForCaching,
  logPaginationResult,
  logSnapshotCached,
  calculateNewSnapshotCache,
  extractParagraphSnapshots
} from './TypesetterClientFunctionClassesObjects/snapshot';
import { SnapshotCacheManager } from './TypesetterClientFunctionClassesObjects/snapshot';
import {
  preloadFontForPagination,
  convertPreloadedFontToPaginationFormat,
  resolveFontMetadata,
  logFontMetadata,
  prepareFontsWithBytes,
  resolveFontUidSafe,
  extractUniqueFonts,
  fontsMapToArray,
  validateFontLoading,
  createFontLoadingErrorMessage
} from './TypesetterClientFunctionClassesObjects/font';
import { FontPreparationCoordinator } from './TypesetterClientFunctionClassesObjects/font';
import {
  getInFlightRequest,
  setInFlightRequest,
  isRequestInFlight,
  removeInFlightRequest,
  getReactiveRequest,
  rejectReactiveRequest,
  resolveReactiveRequest,
  cleanupAllRequests,
  WORKER_TERMINATED_ERROR,
  generateRequestId,
  generateRequestIdWithValue,
  enqueueOperation
} from './TypesetterClientFunctionClassesObjects/queue';
import { LayoutResponseHandler } from './TypesetterClientFunctionClassesObjects/queue';
import { calculateEditDelta } from './TypesetterClientFunctionClassesObjects/edit';
import { detectInitializationState, shouldSkipInitialization } from './TypesetterClientFunctionClassesObjects/document';
import { DocumentInitializationHandler } from './TypesetterClientFunctionClassesObjects/document';
import {
  decodeGlyphPath,
  generateGlyphCacheKey
} from './TypesetterClientFunctionClassesObjects/glyph';
import { GlyphDataProcessor } from './TypesetterClientFunctionClassesObjects/glyph';
import {
  buildParagraphToPageMap,
  mapParagraphIdsToIndices,
  findMinParagraphIndex,
  findParagraphIdWithMinIndex,
  findParagraphSliceInPage
} from './TypesetterClientFunctionClassesObjects/paragraph';
import { ParagraphToPageMapBuilder } from './TypesetterClientFunctionClassesObjects/paragraph';
import { pageHashesToArray } from './TypesetterClientFunctionClassesObjects/utils';
import {
  findMinValueWithDefault,
  sampleMapEntries,
  sortAscending,
  joinArray,
  joinArrayWithFallback,
  calculateDuration,
  formatDuration,
  stringifyWithTruncation,
  truncateString,
  resetStateForErrorRecovery,
  resetStateForReinitialization,
  resetWorkerState,
  validateWorkerReady,
  getWorkerReadinessStatus,
  WORKER_NOT_INITIALIZED_ERROR
} from './TypesetterClientFunctionClassesObjects/utils';
import { detectDocumentHashChange } from './TypesetterClientFunctionClassesObjects/document';
import { createTelemetryData } from './TypesetterClientFunctionClassesObjects/telemetry';

// 🆕 Phase 2 Observability: Import WeBelieveLogger for WASM diagnostic forwarding
import { WeBelieveLogger } from '../../../utils/WeBelieveLogger';
import type { LogLevel } from '../../../utils/WeBelieveLogger.types';

// 🆕 Phase 1: Reactive message types
import type {
  LayoutId,
  FrontendToWasmMessage,
  WasmToFrontendMessage,
  InitMessage,
  ApplyEditMessage,
  RequestPaginationMessage,
  UpdatePageSetupMessage,
  CancelLayoutMessage,
  PageUpdateMessage,
  CaretMapUpdateMessage,
  LayoutProgressMessage,
  LayoutCompleteMessage,
  ErrorMessage as ReactiveErrorMessage,
  RequestParagraphTextMessage,
  ReceiveParagraphTextMessage,
  LayoutTelemetry
} from '../core/types/ReactiveMessages';

export interface TypesetterWorkerOptions {
  onReady?: () => void;
  onError?: (error: Error) => void;
  
  // 🆕 Phase 1: Reactive message callbacks (optional, for streaming updates)
  onPageUpdate?: (msg: PageUpdateMessage) => void;
  onCaretMapUpdate?: (msg: CaretMapUpdateMessage) => void;
  onLayoutProgress?: (msg: LayoutProgressMessage) => void;
  onLayoutComplete?: (msg: LayoutCompleteMessage) => void;
  onRequestParagraphText?: (msg: RequestParagraphTextMessage) => Promise<ReceiveParagraphTextMessage>;
}

// ============================================================================
// 🆕 Phase 2 Observability: WASM Diagnostic Forwarding
// ============================================================================

/**
 * WASM diagnostic event structure (matches Rust DiagnosticEvent)
 */
interface WasmDiagnosticEvent {
  timestamp: number;
  level: string;
  component: string;
  message: string;
  data?: Record<string, unknown>;
  duration_ms?: number;
  source?: string;      // Explicit source location from WASM (e.g., "wasm/src/lib.rs:1402")
  operation?: string;   // Operation name (e.g., "paginate_from_cache")
}

/**
 * Forward WASM diagnostics to WeBelieveLogger
 * 
 * Diagnostics are captured during WASM operations (cache warnings, performance metrics, etc.)
 * and forwarded to the centralized logging system for real-time monitoring.
 * 
 * @param diagnostics Array of diagnostic events from WASM response
 */
function forwardDiagnosticsToLogger(diagnostics: WasmDiagnosticEvent[] | undefined): void {
  if (!diagnostics || diagnostics.length === 0) return;
  
  for (const diag of diagnostics) {
    const level = (diag.level || 'debug') as LogLevel;
    const component = `wasm.${diag.component || 'unknown'}`;
    const message = diag.message || 'Unknown diagnostic';
    const operation = diag.operation || diag.data?.operation as string || message;
    
    // Use explicit source from WASM if available, otherwise create synthetic source
    const wasmSource = diag.source 
      ? { file: diag.source, function: operation }
      : { file: `wasm/${diag.component || 'typesetter'}.rs`, function: operation };
    
    // Include wasmSource in data for non-performance logs
    const data = {
      ...diag.data,
      wasmTimestamp: diag.timestamp,
      durationMs: diag.duration_ms,
      wasmSource: wasmSource.file,  // Include source in data for all log types
    };
    
    // Forward to WeBelieveLogger
    try {
      if (level === 'performance') {
        // Use performanceWithSource to provide meaningful WASM source
        WeBelieveLogger.performanceWithSource(component, operation, diag.duration_ms || 0, wasmSource, data);
      } else {
        // For non-performance logs, use logWithSource to pass the WASM source
        WeBelieveLogger.logWithSource(level, component, message, wasmSource, data);
      }
    } catch (e) {
      // Fallback if WeBelieveLogger not initialized (shouldn't happen in normal flow)
      DEBUG_ENABLED && console.log(`[WASM Diagnostic] [${level}] ${component}: ${message}`, data);
    }
  }
}

export class TypesetterWorkerClient {
  private worker: Worker | null = null;
  private isReady = false;
  private requestIdRef = { value: 0 };
  private glyphCacheCallback?: () => void;
  private latestRequestId = 0; // 🆕 Track latest request to ignore stale responses
  private pendingRequests = new Map<string, {
    resolve: (result: ParagraphSnapshot) => void;
    reject: (error: Error) => void;
    requestId: number; // 🆕 Track request sequence number
  }>();
  
  // 🆕 Store last layout result for retrieval (includes plan_hash)
  private lastLayoutResult: ParagraphSnapshot | null = null;
  
  // ✅ Track which fonts we've sent to the worker (to avoid resending)
  private sentFonts = new Set<string>();
  
  // 🆕 NEW for Phase 2: Page cache for pagination
  private pageCache = new Map<string, PageLayout>(); // key: `${docHash}-${pageIndex}`
  private currentDocHash: string = '';
  
  // 🆕 L2 Persistent Cache: IndexedDB-backed cache orchestrator
  private cacheOrchestrator: CacheOrchestrator | null = null;
  private currentDocumentId: string = ''; // Unique document ID for persistent cache
  private lastPaginationFaultAtByDoc: Map<string, number> = new Map();
  private lastPaginationFaultAtMs: number = 0;
  private currentDocumentText: string = '';
  private currentPageSetup: PageSetup | null = null;
  // Guard to ensure we initialize the document only once per worker lifecycle
  private documentInitialized: boolean = false;
  // Track if initializeDocument is currently in-flight
  private initializeInFlight: Promise<{ version: number }> | null = null;
  // Track last initialized text to avoid redundant reinits
  private lastInitializedText: string = '';
  
  /**
   * Returns the text that WASM currently knows about.
   * This is updated after initializeDocument completes and after each successful applyEditDelta.
   * Use this to check if the Canvas's text ref is in sync with WASM before attempting incremental updates.
   */
  getLastInitializedText(): string {
    return this.lastInitializedText;
  }
  
  /**
   * Manually set the last initialized text.
   * Used during desync recovery to prevent cascading desync detection.
   * The actual WASM state will be corrected when initializeDocument completes.
   */
  setLastInitializedText(text: string): void {
    this.lastInitializedText = text;
  }
  
  // Deduplicate in-flight pagination requests
  private inFlightPaginations = new Map<string, Promise<PageWindowResult>>();
  // Serialize edits/pagination to avoid WASM OOB during concurrent operations
  private editInFlightCount: number = 0;
  private isPaginating: boolean = false;
  private didReinitAfterFault: boolean = false;

  // 🚀 INCREMENTAL LAYOUT: Track document state for incremental updates
  private currentDocumentVersion: number = 0;
  private documentParagraphs: Array<{id: string, graphemeStart: number, graphemeEnd: number, version: number}> = [];
  
  // 🆕 PHASE 2: Paragraph snapshot cache (Option A - Client-Side Caching)
  // Stores ParagraphSnapshot[] after each layout for incremental pagination
  private cachedParagraphSnapshots: ParagraphSnapshot[] = [];
  
  // 🆕 PHASE 2: Paragraph-to-page mapping for SpillFrontier calculation
  // Maps paragraph_id to array of page indices that contain slices of that paragraph
  private paragraphToPageMap = new Map<string, number[]>();
  
  // 🆕 PHASE 2: Page hash storage for early termination
  // Maps page_index to page_hash for hash comparison during incremental pagination
  private pageHashes = new Map<number, string>();
  
  // 🆕 PHASE 2: Total pages count (authoritative after full pagination)
  private currentTotalPages: number = 0;
  
  // 🔥 CIRCUIT BREAKER: Track consecutive WASM crashes to prevent infinite loops
  private consecutiveWasmCrashes: number = 0;
  private readonly MAX_CONSECUTIVE_CRASHES = 3;
  private wasmCrashed: boolean = false;

  // 🚀 PHASE 3: Incremental update with pagination
  // Returns actual PageLayout[] instead of ParagraphSnapshot[]
  async applyIncrementalTextChange(
    oldText: string,
    newText: string,
    changeStart: number,
    changeEnd: number,
    fontFamily: string = 'Inter',
    fontWeight: number = 400,
    fontStyle: 'normal' | 'italic' | 'oblique' = 'normal'
  ): Promise<{
    affectedParagraphIds: string[],
    deletedParagraphIds: string[],  // 🆕 Paragraphs that were deleted/merged
    updatedPages: PageLayout[],  // 🆕 PHASE 3: Now returns PageLayout[]
    cacheUpdated: boolean,
    spillFrontier: { para_ix: number; line_ix: number } | null,  // 🆕 PHASE 3
    totalPages: number,  // 🆕 PHASE 3
    stoppedAtPage: number | null,  // 🆕 PHASE 3: Early termination page
    affectedPageIndices: number[] | null  // 🆕 Page indices that actually changed (hash differs)
  }> {
    // Wait for initializeDocument to complete if it's in-flight
    if (this.initializeInFlight) {
      DEBUG_ENABLED && console.log(`🔄 [TypesetterWorkerClient] Waiting for initializeDocument to complete before applying delta...`);
      await this.initializeInFlight;
      DEBUG_ENABLED && console.log(`🔄 [TypesetterWorkerClient] initializeDocument complete, proceeding with delta`);
    }
    
    // Note: We removed the strict text length check here because:
    // 1. The shared WASM worker's lastInitializedText can get out of sync with the Canvas's text ref
    // 2. VirtualPagesV3 and Canvas both use the same worker but have different text state lifecycles
    // 3. The WASM error "No paragraph found at position X" already catches actual desyncs
    // 4. The recovery mechanism (WasmRecovery) handles these errors gracefully
    
    DEBUG_ENABLED && console.log(`🔄 [TypesetterWorkerClient] Incremental text change:`, {
      oldLen: oldText.length,
      newLen: newText.length,
      changeStart,
      changeEnd
    });
    
    // Calculate the edit delta
    const editDelta = calculateEditDelta(oldText, newText, changeStart, changeEnd);

    DEBUG_ENABLED && console.log(`🔄 [TypesetterWorkerClient] Edit delta:`, {
      position: editDelta.position,
      deleteCount: editDelta.deleteCount,
      insertText: truncateString(editDelta.insertText, 50)
    });

    // Apply the edit delta to WASM document state
    let deltaResult;
    try {
      deltaResult = await this.applyEditDelta(editDelta);
    } catch (error: any) {
      console.error(`🔄 [TypesetterWorkerClient] Delta failed - WASM state out of sync:`, error.message);
      throw error;
    }

    DEBUG_ENABLED && console.log(`🔄 [TypesetterWorkerClient] Delta applied:`, {
      affectedParagraphs: deltaResult.affectedParagraphIds.length,
      boundariesChanged: deltaResult.boundariesChanged,
      newVersion: deltaResult.newVersion
    });
    
    // ✅ CRITICAL: Update lastInitializedText to track WASM's current text state
    // This ensures subsequent applyEditDelta calls pass the sync check
    
    // 🔬 DIAGNOSTIC: Track when lastInitializedText changes
    const oldInitText = this.lastInitializedText;
    console.log('🔬 [TEXT TRACKING] lastInitializedText updated (applyIncrementalTextChange):', {
      source: 'applyIncrementalTextChange',
      oldLength: oldInitText.length,
      newLength: newText.length,
      delta: newText.length - oldInitText.length,
      oldFormFeeds: (oldInitText.match(/\f/g) || []).length,
      newFormFeeds: (newText.match(/\f/g) || []).length,
      timestamp: Date.now()
    });
    
    this.lastInitializedText = newText;

    // If no paragraphs were affected, no layout update needed
    if (deltaResult.affectedParagraphIds.length === 0) {
      DEBUG_ENABLED && console.log(`🔄 [TypesetterWorkerClient] No paragraphs affected - skipping layout`);
      return {
        affectedParagraphIds: [],
        deletedParagraphIds: deltaResult.deletedParagraphIds || [],
        updatedPages: [],
        cacheUpdated: false,
        spillFrontier: null,
        totalPages: this.currentTotalPages,
        stoppedAtPage: null,
        affectedPageIndices: null
      };
    }

    // Layout only the affected paragraphs
    DEBUG_ENABLED && console.log(`🔄 [TypesetterWorkerClient] Calling layoutAffectedParagraphs with:`, deltaResult.affectedParagraphIds);
    const layoutResult = await this.layoutAffectedParagraphs(deltaResult.affectedParagraphIds);

    DEBUG_ENABLED && console.log(`🔄 [TypesetterWorkerClient] Layout completed:`, {
      layouts: layoutResult.layouts.length,
      hasErrors: layoutResult.hasErrors,
      unknownParagraphs: layoutResult.unknownParagraphIds.length,
      layoutsData: layoutResult.layouts
    });

    // 🆕 PHASE 2: Update cached paragraph snapshots
    // layoutResult.layouts is an array of ParagraphSnapshot objects (worker extracts them)
    // OR it could be {paragraphId, cacheHit, snapshot} objects - handle both cases
    
    // Debug: Log the raw layout structure
    DEBUG_ENABLED && console.log(`📊 [Phase2] Raw layouts structure:`, {
      layoutsLength: layoutResult.layouts.length,
      firstLayoutKeys: layoutResult.layouts[0] ? Object.keys(layoutResult.layouts[0]) : [],
      firstLayoutHasSnapshot: !!layoutResult.layouts[0]?.snapshot,
      firstLayoutHasLines: !!layoutResult.layouts[0]?.lines,
      firstLayoutSnapshotKeys: layoutResult.layouts[0]?.snapshot ? Object.keys(layoutResult.layouts[0].snapshot) : [],
      firstLayoutSnapshotHasLines: !!layoutResult.layouts[0]?.snapshot?.lines
    });
    
    // Extract paragraph snapshots from layout results
    const snapshots = extractParagraphSnapshots(layoutResult.layouts);

    DEBUG_ENABLED && console.log(`📊 [Phase2] Extracted ${snapshots.length} snapshots from ${layoutResult.layouts.length} layouts`, {
      firstSnapshotKeys: snapshots[0] ? Object.keys(snapshots[0]) : [],
      firstSnapshotLinesCount: snapshots[0]?.lines?.length
    });
    // ✅ CRITICAL FIX: Update document hash BEFORE storing snapshots
    // This ensures snapshots are stored with the NEW hash, not cleared immediately after
    this.currentDocumentText = newText;
    const newDocHash = computeDocHash(newText, fontFamily, fontWeight, fontStyle);
    const oldDocHash = this.currentDocHash;
    
    if (oldDocHash !== newDocHash) {
      DEBUG_ENABLED && console.log(`🔄 [TypesetterWorkerClient] Document changed - updating doc hash:`, {
        oldHash: oldDocHash.substring(0, 8),
        newHash: newDocHash.substring(0, 8)
      });
      
      // ⚡ PERFORMANCE FIX: For incremental edits, DON'T clear caches aggressively
      // The hash changes on every keystroke, but the page structure is still valid.
      // Only clear the page cache (which has old content), but KEEP:
      // - paragraphToPageMap (paragraph positions in pages are still valid)
      // - pageHashes (can be used for early termination)
      // 
      // This prevents falling back to full initializeDocument on every keystroke.
      
      if (oldDocHash) {
        // Clear page cache - pages have old content
        const oldCacheKeys = filterCacheKeysByDocHash(this.pageCache, oldDocHash);
        for (const key of oldCacheKeys) {
          this.pageCache.delete(key);
        }
        DEBUG_ENABLED && console.log(`🗑️ [TypesetterWorkerClient] Invalidated ${oldCacheKeys.length} pages with old docHash`);
        
        // ⚡ KEEP pageHashes - they're useful for early termination even with new hash
        // The page structure is similar, so hashes can detect unchanged pages
        // this.pageHashes.clear(); // REMOVED - keep for early termination
        
        // 🔥 CRITICAL FIX: Only clear cachedParagraphSnapshots if we have NEW snapshots to replace them.
        // For selection deletes, WASM's layoutAffectedParagraphs may return only a partial snapshot
        // (e.g., 1 line covering the edit region). If we clear the cache unconditionally,
        // calculateNewSnapshotCache has nothing to merge with, and we lose all lines for pages 1-3.
        // 
        // However, we CAN'T keep stale snapshots because their grapheme indices are wrong for the
        // new text. The solution is to check if the new snapshots cover the full document.
        // If not, we'll need to handle this in the merge logic or request a full re-layout.
        //
        // For now, we clear the cache but the merge logic in calculateNewSnapshotCache will
        // handle partial snapshots by preserving lines that come after the new snapshot's range.
        // This works because we pass the OLD cache to calculateNewSnapshotCache BEFORE clearing.
        const oldCacheForMerge = [...this.cachedParagraphSnapshots];
        this.cachedParagraphSnapshots.length = 0;
        DEBUG_ENABLED && console.log(`🗑️ [TypesetterWorkerClient] Cleared old paragraph snapshot cache (saved ${oldCacheForMerge.length} for merge)`);
        
        // 🔥 CRITICAL: Pass old cache to merge function via a temporary storage
        // This allows calculateNewSnapshotCache to merge partial snapshots with old lines
        (this as any)._oldCacheForMerge = oldCacheForMerge;
        
        // ⚡ KEEP paragraphToPageMap - paragraph positions in pages are still valid!
        // Clearing this causes SpillFrontierCalculator to fail, triggering full re-init
        // this.paragraphToPageMap.clear(); // REMOVED - keep for SpillFrontier calculation
        DEBUG_ENABLED && console.log(`✅ [TypesetterWorkerClient] Kept paragraphToPageMap for SpillFrontier (${this.paragraphToPageMap.size} entries)`);
      }
      
      this.currentDocHash = newDocHash;
    }
    
    // ✅ CRITICAL FIX: Store snapshots AFTER updating hash
    // This ensures snapshots are stored with the NEW hash and won't be immediately cleared
    // 🔥 CRITICAL: Pass old cache for merging partial snapshots (selection delete fix)
    let oldCacheForMerge = (this as any)._oldCacheForMerge as ParagraphSnapshot[] | undefined;
    delete (this as any)._oldCacheForMerge; // Clean up temporary storage
    
    // 🔥 FIX (2026-01-13): If old cache paragraph IDs don't match NEW paragraph IDs,
    // the cache is from a previous document state (e.g., delete-all-and-retype scenario).
    // Don't merge - just use WASM output directly as the single source of truth.
    if (oldCacheForMerge && oldCacheForMerge.length > 0 && snapshots.length > 0) {
      const oldParaIds = new Set(oldCacheForMerge.map(s => s.paragraph_id || s.snapshot_id).filter(Boolean));
      const newParaIds = new Set(snapshots.map(s => s.paragraph_id || s.snapshot_id).filter(Boolean));
      
      // Check if ANY old IDs match new IDs
      let hasOverlap = false;
      for (const newId of newParaIds) {
        if (oldParaIds.has(newId)) {
          hasOverlap = true;
          break;
        }
      }
      
      if (!hasOverlap) {
        console.log(`🗑️ [TypesetterWorkerClient] Old cache has different paragraph IDs - discarding (WASM = source of truth)`, {
          oldIds: Array.from(oldParaIds).map(id => id?.substring(0, 8)),
          newIds: Array.from(newParaIds).map(id => id?.substring(0, 8))
        });
        oldCacheForMerge = undefined; // Don't merge stale cache
      }
    }
    
    // 🔥 CRITICAL: Pass edit delta so grapheme indices can be properly adjusted for preserved lines
    // Without this, lines after the edit region have stale indices, causing slice extraction to fail
    // 🔥 FIX (2026-01-12): Include committedLength for filtering stale lines
    const editDeltaInfo = {
      position: editDelta.position,
      deleteCount: editDelta.deleteCount,
      insertLength: editDelta.insertText.length,
      committedLength: newText.length
    };
    
    // 🔥 FIX (2026-01-13): Track document length for cache sanitization
    this.lastKnownDocumentLength = newText.length;
    console.log(`🔬 [applyIncrementalTextChange] Set lastKnownDocumentLength=${this.lastKnownDocumentLength} at ${Date.now()}`);
    
    // 🔥 CRITICAL FIX: Pass boundariesChanged, affectedParagraphIds, deletedParagraphIds, and allParagraphIds
    // When boundaries change (split/merge via \n\n), we need to use mergeSnapshotsByParagraphId
    // instead of calculateNewSnapshotCache to properly handle multiple paragraphs
    // deletedParagraphIds explicitly tells us which paragraphs were merged/removed by WASM
    // allParagraphIds provides definitive validation for cached snapshot filtering
    SnapshotCacheManager.updateCachedSnapshots(
      this.cachedParagraphSnapshots, 
      snapshots, 
      oldCacheForMerge, 
      editDeltaInfo,
      deltaResult.boundariesChanged,
      deltaResult.affectedParagraphIds,
      deltaResult.deletedParagraphIds,
      layoutResult.allParagraphIds
    );

    // 🆕 LINE-LEVEL SPILLFRONTIER: Use edit position for precise line calculation
    // This optimization uses the cached paragraph snapshots (which have line data)
    // to determine exactly which line was affected by the edit, rather than
    // starting from line 0 every time.
    const spillFrontier = SpillFrontierCalculator.calculateSpillFrontierFromEditPosition(
      changeStart,  // Use the edit position for line-level precision
      deltaResult.affectedParagraphIds,
      this.documentParagraphs,
      this.cachedParagraphSnapshots  // Use cached snapshots with line data
    );
    
    // ⏱️ BENCHMARK: Log SpillFrontier calculation (controlled by PRODUCTION_LOGS_ENABLED)
    PRODUCTION_LOGS_ENABLED && console.log(`📊 [Phase3A] SpillFrontier calculated:`, spillFrontier, {
      affectedParagraphIds: deltaResult.affectedParagraphIds,
      documentParagraphsCount: this.documentParagraphs.length,
      paragraphToPageMapSize: this.paragraphToPageMap.size,
      pageCacheSize: this.pageCache.size,
      editPosition: changeStart
    });

    // If no SpillFrontier, fall back to returning snapshots only
    if (!spillFrontier) {
      PRODUCTION_LOGS_ENABLED && console.warn(`⚠️ [Phase3A] No SpillFrontier - returning snapshots only. This triggers full re-init!`);
      return {
        affectedParagraphIds: deltaResult.affectedParagraphIds,
        deletedParagraphIds: deltaResult.deletedParagraphIds || [],
        updatedPages: [],  // Empty - caller should fall back to full pagination
        cacheUpdated: true,
        spillFrontier: null,
        totalPages: this.currentTotalPages,
        stoppedAtPage: null,
        affectedPageIndices: null
      };
    }

    // 🆕 PHASE 3A: Call lightweight paginateFromCache
    // WASM reads paragraphs from its internal cache - NO JSON transfer!
    
    // 🆕 FIX: Calculate startPageIndex from EDIT POSITION using form feed count
    // The pageCache is cleared on every edit (hash changes), so we can't use it.
    // Instead, we count form feeds before the edit position to determine the page.
    let startPageIndex = 0;
    
    // Strategy: Count form feeds (\f) before changeStart to determine page index
    // Each \f marks a page break, so the number of \f before the edit = page index
    // CRITICAL: Use oldText (the parameter) because changeStart is a position in oldText.
    // oldText is the document state BEFORE the edit was applied.
    const textBeforeEdit = oldText.substring(0, changeStart);
    const formFeedCount = (textBeforeEdit.match(/\f/g) || []).length;
    
    // 🔬 DEBUG: Log form feed positions to diagnose Page 3 edit issue
    const allFormFeedPositions: number[] = [];
    for (let i = 0; i < oldText.length; i++) {
      if (oldText[i] === '\f') {
        allFormFeedPositions.push(i);
      }
    }
    console.log(`🔬 [Phase3A DEBUG] Form feed analysis:`, {
      changeStart,
      oldTextLength: oldText.length,
      textBeforeEditLength: textBeforeEdit.length,
      formFeedCount,
      totalFormFeedsInOldText: allFormFeedPositions.length,
      formFeedPositions: allFormFeedPositions,
      charAtChangeStart: oldText[changeStart] || '(out of bounds)',
      charBeforeChangeStart: oldText[changeStart - 1] || '(out of bounds)'
    });
    
    // Ensure startPageIndex is never negative (can happen if currentTotalPages is 0)
    const maxPageIndex = Math.max(0, this.currentTotalPages - 1);
    startPageIndex = Math.min(formFeedCount, maxPageIndex);
    
    // 🔥 CRITICAL FIX: When startPageIndex > 0, we need to adjust spillFrontier.line_ix
    // to point to the first line of that page, not line 0.
    // 
    // The issue: When editing on Page 1, spillFrontier.line_ix might be 0 (start of paragraph),
    // but Page 1's content starts at line 3 (for example). If we pass line_ix=0 to WASM,
    // it will create Page 1 with lines 0-3, which is Page 0's content!
    //
    // Solution: For single-paragraph documents spanning multiple pages, we need to find
    // the line index where the target page starts. We can do this by looking at the
    // cached paragraph snapshot and finding the line that contains the first character
    // after the form feed.
    let adjustedSpillFrontier = { ...spillFrontier };
    
    // 🔥 CRITICAL FIX: When startPageIndex === 0, ALWAYS start from line 0
    // The spillFrontier.line_ix from SpillFrontierCalculator tells us which line
    // contains the edit, but for Page 0 we must always start pagination from line 0
    // to ensure all lines are included. Otherwise, Page 0 would skip lines before
    // the edit position.
    if (startPageIndex === 0) {
      adjustedSpillFrontier.line_ix = 0;
    } else if (startPageIndex > 0 && this.cachedParagraphSnapshots.length > 0) {
      // Find the position of the Nth form feed (where N = startPageIndex)
      let formFeedPosition = -1;
      let ffCount = 0;
      for (let i = 0; i < oldText.length; i++) {
        if (oldText[i] === '\f') {
          ffCount++;
          if (ffCount === startPageIndex) {
            formFeedPosition = i;
            break;
          }
        }
      }
      
      if (formFeedPosition >= 0) {
        // Find which line contains the character after the form feed
        // The first character of the target page is at formFeedPosition + 1 (after \f)
        // or formFeedPosition + 2 (after \f\n)
        const targetPosition = formFeedPosition + 1; // Position after form feed
        
        const snapshot = this.cachedParagraphSnapshots[0];
        if (snapshot?.lines) {
          for (let lineIdx = 0; lineIdx < snapshot.lines.length; lineIdx++) {
            const line = snapshot.lines[lineIdx];
            // Check if this line contains or starts after the target position
            if (line.end_grapheme_index > targetPosition || 
                (lineIdx === snapshot.lines.length - 1)) {
              adjustedSpillFrontier.line_ix = lineIdx;
              console.log(`🔧 [Phase3A] Adjusted spillFrontier.line_ix for page ${startPageIndex}:`, {
                originalLineIx: spillFrontier.line_ix,
                adjustedLineIx: lineIdx,
                formFeedPosition,
                targetPosition,
                lineStartGrapheme: line.start_grapheme_index,
                lineEndGrapheme: line.end_grapheme_index
              });
              break;
            }
          }
        }
      }
    }
    
    console.log(`📊 [Phase3A] startPageIndex calculation:`, {
      changeStart,
      startPageIndex,
      formFeedCount,
      currentTotalPages: this.currentTotalPages,
      maxPageIndex,
      textBeforeEditLength: textBeforeEdit.length,
      oldTextLength: oldText.length,
      newTextLength: newText.length,
      originalLineIx: spillFrontier.line_ix,
      adjustedLineIx: adjustedSpillFrontier.line_ix
    });
    
    // 🔥 CRITICAL FIX: Disable early termination (hash checking) for ALL edit operations
    // When editing content on any page, the line indices for ALL subsequent pages shift.
    // Even if a page's content (and hash) doesn't change, its slice bounds (line_start,
    // line_end) become stale because they're based on the OLD line indices.
    // 
    // For example, if Page 1 has lines 1-3 and we insert a line on Page 0:
    // - Page 1's content is still the same (same hash)
    // - But Page 1's slice bounds should now be lines 2-4, not 1-3!
    // 
    // By disabling hash checking for ALL edits, WASM returns ALL pages from the edit point
    // to the end, ensuring the frontend has fresh slice bounds for every page.
    const isDeleteOperation = editDelta.deleteCount > 0 && editDelta.insertText.length === 0;
    const useExistingHashes = false;  // 🔥 ALWAYS disable hash checking to get fresh slice bounds
    
    PRODUCTION_LOGS_ENABLED && console.log(`📊 [Phase3A] Calling paginateFromCache (lightweight):`, {
      spillFrontier: adjustedSpillFrontier,
      startPageIndex,  // 🆕 Log the calculated start page
      isDeleteOperation,
      useExistingHashes,
      note: 'NO paragraph data sent - WASM uses internal cache'
    });

    try {
      const startTime = performance.now();
      
      const paginationResult = await this.paginateFromCache(
        adjustedSpillFrontier,  // 🔥 Use adjusted spillFrontier with correct line_ix
        100,  // Max pages
        useExistingHashes,  // 🔥 FIX: Disable hash checking for delete operations
        startPageIndex  // 🆕 FIX: Pass start page index
      );

      // 🔬 DEBUG: Log what WASM returned for pages
      console.log(`🔬 [Phase3A] WASM paginateFromCache returned ${paginationResult.pages.length} pages`);
      for (const page of paginationResult.pages) {
        const pageLines = (page as any).lines ?? [];
        console.log(`🔬 [Phase3A] WASM page ${(page as any).page_index}: ${pageLines.length} lines`, {
          firstLines: pageLines.slice(0, 5).map((l: any) => `[${l.start_grapheme_index}-${l.end_grapheme_index})`).join(', '),
          maxGrapheme: pageLines.length > 0 ? Math.max(...pageLines.map((l: any) => l.end_grapheme_index)) : 0
        });
      }

      const duration = calculateDuration(startTime, performance.now());

      // ⏱️ BENCHMARK: Log incremental pagination result (controlled by PRODUCTION_LOGS_ENABLED)
      PRODUCTION_LOGS_ENABLED && console.log(`📊 [Phase3A] Incremental pagination complete:`, {
        pagesReturned: paginationResult.pages.length,
        totalPages: paginationResult.total_pages,
        stoppedAtPage: paginationResult.stopped_at_page,
        paragraphsUsed: paginationResult.paragraphs_used,
        durationMs: formatDuration(duration)
      });

      // Merge new pages into cache
      this.currentTotalPages = PageCacheMerger.mergePagesIntoCache(
        paginationResult.pages,
        adjustedSpillFrontier,  // 🔥 Use adjusted spillFrontier
        this.pageCache,
        this.currentDocHash,
        this.pageHashes,
        this.currentTotalPages
      );
      
      // 🆕 L2 PERSISTENT CACHE: Also save to IndexedDB for fast document re-open
      if (this.cacheOrchestrator && paginationResult.pages.length > 0) {
        // Fire and forget - don't block on IndexedDB write
        this.cacheOrchestrator.setPages(paginationResult.pages).catch(err => {
          DEBUG_ENABLED && console.warn('[TypesetterWorkerClient] L2 cache write failed:', err);
        });
      }
      
      // ✅ OPTION B FIX: Validate totalPages using form feed count
      // WASM incremental pagination may return incorrect total_pages when editing
      // on pages other than page 0. Use form feed count as ground truth.
      const expectedPagesFromFormFeeds = (newText.match(/\f/g) || []).length + 1;
      const wasmTotalPages = paginationResult.total_pages;
      
      // 🔥 CRITICAL FIX: For DELETE operations, form feed count is authoritative
      // When a page break is deleted, page count MUST decrease. Using Math.max
      // would incorrectly preserve the old (higher) page count.
      // For INSERT operations, use Math.max to ensure we don't lose pages.
      let validatedTotalPages: number;
      if (isDeleteOperation) {
        // DELETE: Form feed count is authoritative - pages can decrease
        validatedTotalPages = expectedPagesFromFormFeeds;
        DEBUG_ENABLED && console.log(`📊 [Option B] DELETE operation: Using form feed count as authoritative: ${expectedPagesFromFormFeeds} (was ${this.currentTotalPages})`);
      } else {
        // INSERT: Use max to ensure we don't lose pages
        validatedTotalPages = Math.max(
          wasmTotalPages,
          this.currentTotalPages,
          expectedPagesFromFormFeeds
        );
      }
      
      // Update our tracked count
      if (validatedTotalPages !== this.currentTotalPages) {
        DEBUG_ENABLED && console.log(`📊 [Option B] Correcting totalPages: WASM=${wasmTotalPages}, tracked=${this.currentTotalPages}, formFeeds=${expectedPagesFromFormFeeds} → ${validatedTotalPages}`);
        this.currentTotalPages = validatedTotalPages;
      }

      DEBUG_ENABLED && console.log(`✅ [Phase3A] Lightweight pagination complete - ${paginationResult.pages.length} pages in ${duration.toFixed(0)}ms`);

      return {
        affectedParagraphIds: deltaResult.affectedParagraphIds,
        deletedParagraphIds: deltaResult.deletedParagraphIds || [],
        updatedPages: paginationResult.pages,
        cacheUpdated: true,
        spillFrontier,
        totalPages: this.currentTotalPages,  // ✅ Use validated count, not raw WASM result
        stoppedAtPage: paginationResult.stopped_at_page ?? null,
        affectedPageIndices: paginationResult.affected_page_indices ?? null  // 🆕 Only changed pages
      };
    } catch (error: any) {
      console.error(`❌ [Phase3A] Lightweight pagination failed:`, error.message, error.stack);
      // Fall back to returning empty pages - caller will do full pagination
      return {
        affectedParagraphIds: deltaResult.affectedParagraphIds,
        deletedParagraphIds: deltaResult.deletedParagraphIds || [],
        updatedPages: [],
        cacheUpdated: true,
        spillFrontier,
        totalPages: this.currentTotalPages,
        stoppedAtPage: null,
        affectedPageIndices: null
      };
    }
  }

  // ============================================================================
  // 🆕 OPTION C: WASM as Single Source of Truth
  // ============================================================================
  
  /**
   * Edit command types for Option C architecture
   */
  public static readonly EditCommandType = {
    INSERT: 'insert',
    DELETE: 'delete',
    REPLACE: 'replace'
  } as const;
  
  /**
   * Apply an edit command to WASM and return the new text + cursor position.
   * This is the core of Option C - WASM is the single source of truth.
   * 
   * @param command - The edit command to apply
   * @returns The new text, cursor position, and layout result from WASM
   */
  async applyEditCommand(command: {
    type: 'insert' | 'delete' | 'replace';
    position: number;  // Grapheme index
    text?: string;     // For insert/replace
    deleteCount?: number;  // For delete/replace (grapheme count)
    fontFamily?: string;
    fontWeight?: number;
    fontStyle?: 'normal' | 'italic' | 'oblique';
  }): Promise<{
    success: boolean;
    newText: string;
    newCursorPosition: number;  // Grapheme index
    affectedParagraphIds: string[];
    deletedParagraphIds: string[];  // 🆕 Paragraphs that were deleted/merged
    updatedPages: PageLayout[];
    totalPages: number;
    affectedPageIndices?: number[] | null;  // 🆕 Only changed pages (from WASM hash comparison)
    error?: string;
  }> {
    const startTime = performance.now();
    const { type, position, text = '', deleteCount = 0, fontFamily = 'Inter', fontWeight = 400, fontStyle = 'normal' } = command;
    
    // Wait for any in-flight initialization
    if (this.initializeInFlight) {
      DEBUG_ENABLED && console.log(`🔄 [Option C] Waiting for initializeDocument to complete...`);
      await this.initializeInFlight;
    }
    
    // ✅ GUARD: If document is not initialized, fail immediately
    // This prevents "No paragraph found at position X" errors
    if (!this.documentInitialized) {
      console.warn(`⚠️ [Option C] Document not initialized - cannot apply edit command`);
      return {
        success: false,
        newText: '',
        newCursorPosition: position,
        affectedParagraphIds: [],
        deletedParagraphIds: [],
        updatedPages: [],
        totalPages: this.currentTotalPages,
        affectedPageIndices: null,
        error: 'Document not initialized'
      };
    }
    
    // Get current text from WASM
    const oldText = this.lastInitializedText;
    
    // 🔍 DEBUG: Log the state at command processing time
    const hasFormFeed = text?.includes('\f') || text?.includes('\x0C');
    DEBUG_ENABLED && console.log(`🔍 [Option C] applyEditCommand:`, {
      type,
      position,
      text: text?.slice(0, 20),
      textCharCodes: text?.split('').map(c => c.charCodeAt(0)),
      hasFormFeed,
      isPageBreak: hasFormFeed,
      deleteCount,
      oldTextLength: oldText.length,
      oldTextPreview: oldText.slice(0, 30),
      documentInitialized: this.documentInitialized
    });
    
    // Calculate the new text and cursor position based on command type
    let newText: string;
    let newCursorPosition: number;
    let changeStart: number;
    let changeEnd: number;
    
    // Convert grapheme position to UTF-16 position for string operations
    // For now, we assume 1:1 mapping (ASCII). Full grapheme support requires WASM mapping.
    const utf16Position = position;  // TODO: Use WASM grapheme-to-UTF16 mapping
    
    switch (type) {
      case 'insert':
        newText = oldText.slice(0, utf16Position) + text + oldText.slice(utf16Position);
        newCursorPosition = position + this.countGraphemes(text);
        changeStart = utf16Position;
        changeEnd = utf16Position;
        break;
        
      case 'delete':
        // Delete `deleteCount` graphemes starting at position
        const deleteEndUtf16 = utf16Position + deleteCount;  // TODO: Use proper grapheme mapping
        
        // 🔬 DIAGNOSTIC: Log exactly what is being deleted
        const charBeingDeleted = oldText.charAt(utf16Position);
        const charTypeDeleted = charBeingDeleted === '\f' ? 'FORM_FEED' : 
                                charBeingDeleted === '\n' ? 'NEWLINE' : 
                                charBeingDeleted === ' ' ? 'SPACE' : `'${charBeingDeleted}'`;
        console.log('🔬 [WASM DELETE DIAGNOSTIC] About to delete:', {
          position: utf16Position,
          deleteCount,
          deleteEndUtf16,
          charBeingDeleted: charTypeDeleted,
          charCodeBeingDeleted: charBeingDeleted.charCodeAt(0),
          oldTextLength: oldText.length,
          willResultInLength: oldText.length - deleteCount,
          formFeedsBefore: (oldText.match(/\f/g) || []).length,
          willDeleteFormFeed: charBeingDeleted === '\f',
          textAroundDelete: JSON.stringify(oldText.slice(Math.max(0, utf16Position - 3), utf16Position + 3)),
          timestamp: Date.now()
        });
        
        newText = oldText.slice(0, utf16Position) + oldText.slice(deleteEndUtf16);
        
        // 🔬 DIAGNOSTIC: Log result of delete
        console.log('🔬 [WASM DELETE DIAGNOSTIC] After delete:', {
          newTextLength: newText.length,
          charsActuallyDeleted: oldText.length - newText.length,
          formFeedsAfter: (newText.match(/\f/g) || []).length,
          formFeedsRemoved: (oldText.match(/\f/g) || []).length - (newText.match(/\f/g) || []).length,
          textPreview: JSON.stringify(newText.slice(0, 30))
        });
        
        newCursorPosition = position;
        changeStart = utf16Position;
        changeEnd = deleteEndUtf16;
        break;
        
      case 'replace':
        // Replace `deleteCount` graphemes with `text`
        const replaceEndUtf16 = utf16Position + deleteCount;  // TODO: Use proper grapheme mapping
        newText = oldText.slice(0, utf16Position) + text + oldText.slice(replaceEndUtf16);
        newCursorPosition = position + this.countGraphemes(text);
        changeStart = utf16Position;
        changeEnd = replaceEndUtf16;
        break;
        
      default:
        return {
          success: false,
          newText: oldText,
          newCursorPosition: position,
          affectedParagraphIds: [],
          deletedParagraphIds: [],
          updatedPages: [],
          totalPages: this.currentTotalPages,
          error: `Unknown command type: ${type}`
        };
    }
    
    // ✅ LARGE PASTE DETECTION: If the change is very large, skip incremental update
    // and force a full document re-initialization. This is necessary because:
    // 1. Incremental updates assume the paragraph structure is already known
    // 2. Large pastes can introduce many new paragraphs that WASM doesn't know about
    // 3. The error "No paragraph found at position X" occurs when WASM's internal
    //    paragraph structure doesn't cover the edit position
    const insertionSize = text.length;
    const isLargePaste = insertionSize > 100; // Threshold: 100 characters
    
    if (isLargePaste) {
      console.log(`📋 [Option C] Large paste detected (${insertionSize} chars) - forcing full re-initialization`);
      
      try {
        // Build paragraph structure for the new text
        const paragraphs = this.buildParagraphsFromText(newText, fontFamily, fontWeight, fontStyle);
        
        // Convert page setup to the format expected by initializeDocument
        const convertedPageSetup = this.convertPageSetupForInit(this.currentPageSetup!);
        
        // Force re-initialize document with the new text
        await this.initializeDocument({
          text: newText,
          paragraphs,
          pageSetup: convertedPageSetup,
          fonts: []  // Fonts will be resolved by initializeDocument
        }, true);  // Force re-initialization
        
        // Get pagination result for the new document
        const paginationResult = await this.getVisiblePages(
          newText,
          this.currentPageSetup!,
          [0, 10],  // Get first 10 pages
          fontFamily,
          14,  // TODO: Get actual font size from somewhere
          2,   // Lookahead
          fontWeight,
          fontStyle
        );
        
        const duration = performance.now() - startTime;
        console.log(`✅ [Option C] Large paste re-initialization complete:`, {
          newTextLength: newText.length,
          totalPages: paginationResult.total_pages,
          durationMs: duration.toFixed(2)
        });
        
        return {
          success: true,
          newText,
          newCursorPosition,
          affectedParagraphIds: paragraphs.map(p => p.id),
          deletedParagraphIds: [],  // Full re-init doesn't track deletions
          updatedPages: paginationResult.pages || [],
          totalPages: paginationResult.total_pages || 1,
          affectedPageIndices: null  // Full re-init, all pages affected
        };
      } catch (error: any) {
        console.error(`❌ [Option C] Large paste re-initialization failed:`, error.message);
        return {
          success: false,
          newText: oldText,
          newCursorPosition: position,
          affectedParagraphIds: [],
          deletedParagraphIds: [],
          updatedPages: [],
          totalPages: this.currentTotalPages,
          affectedPageIndices: null,
          error: error.message
        };
      }
    }
    
    try {
      // Apply the change to WASM using existing incremental update path
      const result = await this.applyIncrementalTextChange(
        oldText,
        newText,
        changeStart,
        changeEnd,
        fontFamily,
        fontWeight,
        fontStyle
      );
      
      const duration = performance.now() - startTime;
      
      // 🔍 DEBUG: Check if page break was detected
      const newTextHasFormFeed = newText.includes('\f');
      if (newTextHasFormFeed) {
        console.log(`📄 [Option C] PAGE BREAK DEBUG:`, {
          newTextHasFormFeed,
          formFeedPosition: newText.indexOf('\f'),
          totalPages: result.totalPages,
          pagesReturned: result.updatedPages.length,
          pageIndicesReturned: result.updatedPages.map((p: any) => p.page_index),
          newTextLength: newText.length,
          newTextPreview: newText.slice(0, 50).replace(/\f/g, '[FF]').replace(/\n/g, '[NL]')
        });
      }
      
      // Log for Option C assessment (controlled by PRODUCTION_LOGS_ENABLED)
      PRODUCTION_LOGS_ENABLED && console.log(`✅ [Option C] Edit command applied:`, {
        type,
        position,
        textLength: text.length,
        deleteCount,
        oldTextLength: oldText.length,
        newTextLength: newText.length,
        newCursorPosition,
        durationMs: duration.toFixed(2),
        affectedParagraphs: result.affectedParagraphIds.length,
        pagesUpdated: result.updatedPages.length
      });
      
      return {
        success: true,
        newText,
        newCursorPosition,
        affectedParagraphIds: result.affectedParagraphIds,
        deletedParagraphIds: result.deletedParagraphIds || [],
        updatedPages: result.updatedPages,
        totalPages: result.totalPages,
        affectedPageIndices: result.affectedPageIndices  // 🆕 From WASM hash comparison
      };
    } catch (error: any) {
      console.error(`❌ [Option C] Edit command failed:`, error.message);
      return {
        success: false,
        newText: oldText,
        newCursorPosition: position,
        affectedParagraphIds: [],
        deletedParagraphIds: [],
        updatedPages: [],
        totalPages: this.currentTotalPages,
        affectedPageIndices: null,
        error: error.message
      };
    }
  }
  
  /**
   * Count grapheme clusters in a string (for cursor positioning)
   * Uses Intl.Segmenter for accurate grapheme segmentation
   */
  private countGraphemes(text: string): number {
    // Use Intl.Segmenter if available (modern browsers - ES2022)
    // @ts-ignore - Intl.Segmenter is a newer API not in all TypeScript versions
    if (typeof Intl !== 'undefined' && typeof Intl.Segmenter !== 'undefined') {
      // @ts-ignore
      const segmenter = new Intl.Segmenter('en', { granularity: 'grapheme' });
      const segments = Array.from(segmenter.segment(text));
      return segments.length;
    }
    // Fallback: simple character count (works for ASCII)
    return text.length;
  }
  
  /**
   * Get the current text from WASM (for Option C clients that need to read state)
   */
  getCurrentText(): string {
    return this.lastInitializedText;
  }
  
  /**
   * Build paragraph structure from text for initialization
   * 
   * This is a simplified paragraph builder that creates a single paragraph
   * for each newline-separated block. In a production system, this would
   * be more sophisticated and consider block-level AST nodes.
   */
  private buildParagraphsFromText(
    text: string,
    _fontFamily: string,
    _fontWeight: number,
    _fontStyle: 'normal' | 'italic' | 'oblique'
  ): Array<{
    id: string;
    type: string;
    graphemeStart: number;
    graphemeEnd: number;
    version: number;
  }> {
    const paragraphs: Array<{
      id: string;
      type: string;
      graphemeStart: number;
      graphemeEnd: number;
      version: number;
    }> = [];
    
    // Split by newlines to create paragraphs
    const lines = text.split('\n');
    let graphemeOffset = 0;
    
    for (let i = 0; i < lines.length; i++) {
      const lineText = lines[i];
      const lineGraphemes = this.countGraphemes(lineText);
      
      // Add the newline character to grapheme count (except for last line)
      const includesNewline = i < lines.length - 1;
      const totalGraphemes = lineGraphemes + (includesNewline ? 1 : 0);
      
      // Generate unique ID using timestamp and index
      const paragraphId = `${Date.now().toString(16).padStart(16, '0')}-${i.toString(16).padStart(4, '0')}-0000-0000-000000000000`;
      
      paragraphs.push({
        id: paragraphId,
        type: 'paragraph',  // Required field
        graphemeStart: graphemeOffset,
        graphemeEnd: graphemeOffset + totalGraphemes,
        version: 1
      });
      
      graphemeOffset += totalGraphemes;
    }
    
    // Handle edge case: empty text should still have one empty paragraph
    if (paragraphs.length === 0) {
      paragraphs.push({
        id: `${Date.now().toString(16).padStart(16, '0')}-0000-0000-0000-000000000000`,
        type: 'paragraph',
        graphemeStart: 0,
        graphemeEnd: 0,
        version: 1
      });
    }
    
    DEBUG_ENABLED && console.log(`📊 [buildParagraphsFromText] Created ${paragraphs.length} paragraphs from ${text.length} chars`);
    
    return paragraphs;
  }
  
  /**
   * Convert PageSetup type to initializeDocument format
   */
  private convertPageSetupForInit(pageSetup: PageSetup): {
    pageSize: { width: number; height: number };
    orientation: 'portrait' | 'landscape';
    marginsTop: number;
    marginsRight: number;
    marginsBottom: number;
    marginsLeft: number;
    columns: number;
    contentRectXFp: number;
    contentRectYFp: number;
    contentRectWFp: number;
    contentRectHFp: number;
  } {
    // Convert margins from fixed-point to pixels (divide by 64)
    const FP_SCALE = 64;
    
    // Extract page dimensions from page_size
    let width = 816;  // Default: Letter width
    let height = 1056;  // Default: Letter height
    
    if (typeof pageSetup.page_size === 'object') {
      // Custom size with w_px/h_px (as defined in PageSize type)
      const customSize = pageSetup.page_size as { w_px: number; h_px: number };
      if (customSize.w_px) width = customSize.w_px;
      if (customSize.h_px) height = customSize.h_px;
    } else if (typeof pageSetup.page_size === 'string') {
      // Named page size - use defaults based on name
      switch (pageSetup.page_size.toLowerCase()) {
        case 'letter':
          width = 816; height = 1056;
          break;
        case 'a4':
          width = 794; height = 1123;  // A4 in pixels at 96 DPI
          break;
        case 'legal':
          width = 816; height = 1344;
          break;
        default:
          // Keep Letter defaults
          break;
      }
    }
    
    return {
      pageSize: { width, height },
      orientation: pageSetup.orientation === 'landscape' ? 'landscape' : 'portrait',
      marginsTop: pageSetup.margins_fp.top / FP_SCALE,
      marginsRight: pageSetup.margins_fp.right / FP_SCALE,
      marginsBottom: pageSetup.margins_fp.bottom / FP_SCALE,
      marginsLeft: pageSetup.margins_fp.left / FP_SCALE,
      columns: pageSetup.columns || 1,
      contentRectXFp: pageSetup.content_rect_fp.x_fp,
      contentRectYFp: pageSetup.content_rect_fp.y_fp,
      contentRectWFp: pageSetup.content_rect_fp.w_fp,
      contentRectHFp: pageSetup.content_rect_fp.h_fp
    };
  }
  
  // Simple async serialization queue to prevent concurrent edit/paginate
  private operationQueueRef = { current: Promise.resolve() as Promise<void> };

  private enqueue<T>(op: () => Promise<T>): Promise<T> {
    return enqueueOperation(this.operationQueueRef, op);
  }
  
  private options: TypesetterWorkerOptions;
  
  // 🆕 Phase 1: Reactive message tracking
  private nextLayoutId: LayoutId = 1;
  private reactiveRequests = new Map<LayoutId, {
    resolve: (result: any) => void;
    reject: (error: Error) => void;
    timeout: NodeJS.Timeout;
    startTime: number;
    operation: string;
  }>();
  private cancelledLayoutIds = new Set<LayoutId>();
  
  // 🆕 Phase 1: Telemetry collection
  private telemetryData: Map<LayoutId, Partial<LayoutTelemetry>> = new Map();
  
  constructor(options: TypesetterWorkerOptions = {}) {
    this.options = options;
  }
  
  /**
   * Initialize the worker
   */
  async initialize(): Promise<void> {
    if (this.worker) {
      DEBUG_ENABLED && console.warn('[TypesetterWorkerClient] Already initialized');
      return;
    }
    
    DEBUG_ENABLED && console.log('🚀 [TypesetterWorkerClient] Initializing worker...');
    
    try {
    // Import worker using Vite's ?worker suffix for proper transpilation
    // @ts-ignore - Vite handles this special import
    const TypesetterWorker = await import('./typesetter.worker.v5.ts?worker');
      
      DEBUG_ENABLED && console.log('🔧 [TypesetterWorkerClient] Creating worker from Vite bundle...');
      
      this.worker = new TypesetterWorker.default();
      // Expose for engine-driven glyph path warm/get in EdgeDocsCanvas
      try { (globalThis as any).__typesetterWorker = this.worker; } catch {}
      
      // Set up message handler
      this.worker.onmessage = this.handleMessage.bind(this);
      
      // Set up error handler
      this.worker.onerror = (error) => {
        console.error('❌ [TypesetterWorkerClient] Worker error:', error);
        this.options.onError?.(new Error(error.message));
        
        // ✅ NEW: Clear sent fonts cache on worker error (worker may have restarted)
        DEBUG_ENABLED && console.warn('⚠️ [TypesetterWorkerClient] Clearing state due to worker error');
        resetStateForErrorRecovery({
          pageCache: this.pageCache,
          sentFonts: this.sentFonts,
          currentDocHash: this.currentDocHash,
          documentInitialized: this.documentInitialized,
          isReady: this.isReady
        });
      };
      
      // Wait for ready message
      await new Promise<void>((resolve, reject) => {
        const readyTimeout = setTimeout(() => {
          reject(new Error('Worker initialization timeout'));
        }, 10000); // 10 second timeout
        
        const checkReady = (event: MessageEvent) => {
          if (event.data.type === 'ready') {
            clearTimeout(readyTimeout);
            this.worker?.removeEventListener('message', checkReady);
            this.isReady = true;
            DEBUG_ENABLED && console.log('✅ [TypesetterWorkerClient] Worker ready');
            this.options.onReady?.();
            resolve();
          } else if (event.data.type === 'error') {
            clearTimeout(readyTimeout);
            this.worker?.removeEventListener('message', checkReady);
            reject(new Error(event.data.error));
          }
        };
        
        this.worker?.addEventListener('message', checkReady);
      });
      
    } catch (error) {
      console.error('❌ [TypesetterWorkerClient] Initialization failed:', error);
      this.options.onError?.(error as Error);
      throw error;
    }
  }
  
  /**
   * Layout a paragraph (with automatic font loading)
   */
  async layoutParagraph(input: ParagraphLayoutInput): Promise<ParagraphSnapshot> {
    DEBUG_ENABLED && console.log('📨 [TypesetterWorkerClient] layoutParagraph() called', {
      hasWorker: !!this.worker,
      isReady: this.isReady,
      inputTextLength: input.text_runs[0]?.text.length || 0
    });
    
    if (!this.worker || !this.isReady) {
      console.error('❌ [TypesetterWorkerClient] Worker not ready!', getWorkerReadinessStatus(this.worker, this.isReady));
      throw new Error(WORKER_NOT_INITIALIZED_ERROR);
    }
    
    // ✅ PHASE 3: Download font bytes before layout
    // Extract unique fonts from text_runs
    const uniqueFonts = extractUniqueFonts(input.text_runs);
    
    DEBUG_ENABLED && console.log(`📦 [TypesetterWorkerClient] Downloading ${uniqueFonts.size} unique fonts...`);
    
    // Download all fonts in parallel
    let fonts: FontData[] = [];
    const failedFonts: string[] = [];
    
    try {
      fonts = await FontDownloadService.preloadFonts(fontsMapToArray(uniqueFonts));
      DEBUG_ENABLED && console.log(`✅ [TypesetterWorkerClient] Downloaded ${fonts.length}/${uniqueFonts.size} fonts`);
      
      // ✅ NEW: Check if all fonts loaded successfully
      const validationResult = validateFontLoading(fonts, uniqueFonts);
      if (!validationResult.allLoaded) {
        failedFonts.push(...validationResult.failedFonts);
        console.error(`❌ [TypesetterWorkerClient] Font loading incomplete: ${validationResult.missingCount} fonts failed`);
        console.error(`   Failed fonts: ${joinArray(validationResult.failedFonts)}`);
        console.error(`   Loaded fonts: ${joinArray(validationResult.loadedFonts)}`);
      }
    } catch (error) {
      console.error('❌ [TypesetterWorkerClient] Font download failed:', error);
      
      // ✅ NEW: Propagate error to UI instead of silent failure
      const errorMsg = `Failed to load required fonts: ${(error as Error).message}. Document may not render correctly.`;
      console.error(`❌ [TypesetterWorkerClient] ${errorMsg}`);
      
      // Notify via error handler if available
      this.options.onError?.(new Error(errorMsg));
      
      // Still throw to prevent layout with missing fonts
      throw new Error(errorMsg);
    }
    
    // ✅ No new fonts to send, just send layout request without fonts
    const inputWithoutFonts: ParagraphLayoutInput = {
      ...input,
      fonts: [] // Worker will use cached fonts
    };
    
    DEBUG_ENABLED && console.log('✉️ [TypesetterWorkerClient] Sending layout request to worker (using cached fonts)...');
    
    return new Promise((resolve, reject) => {
      const { id, value: currentRequestId } = generateRequestIdWithValue(this.requestIdRef, 'layout');
      this.latestRequestId = currentRequestId; // 🆕 Track as latest request
      
      // Store pending request with sequence number
      this.pendingRequests.set(id, { resolve, reject, requestId: currentRequestId });
      
      DEBUG_ENABLED && console.log(`🔢 [TypesetterWorkerClient] Layout request #${currentRequestId} (${id})`);
      
      // Send request to worker
      this.worker!.postMessage({
        type: 'layout',
        payload: { id, input: inputWithoutFonts }
      });
      
      // Set timeout
      setTimeout(() => {
        if (this.pendingRequests.has(id)) {
          this.pendingRequests.delete(id);
          reject(new Error('Layout request timeout'));
        }
      }, 5000); // 5 second timeout
    });
  }
  
  /**
   * 🆕 NEW for Phase 2: Layout and paginate document (low-level)
   * 
   * Calls WASM pagination directly. For most use cases, use getVisiblePages() instead.
   */
  // 🚀 INCREMENTAL LAYOUT: Apply edit delta to document state
  async applyEditDelta(delta: { position: number, deleteCount: number, insertText: string }): Promise<{
    affectedParagraphIds: string[],
    deletedParagraphIds: string[],
    newVersion: number,
    boundariesChanged: boolean
  }> {
    if (!this.worker || !this.isReady) {
      throw new Error(WORKER_NOT_INITIALIZED_ERROR);
    }

    const requestId = generateRequestId(this.requestIdRef, 'edit');
    const worker = this.worker;

    return new Promise((resolve, reject) => {
      const timeout = setTimeout(() => {
        reject(new Error('Edit delta timeout (15s)'));
      }, 15000);

      const handler = (e: MessageEvent) => {
        const { type, layoutId, error, affectedParagraphIds, deletedParagraphIds, newVersion, boundariesChanged } = e.data;

        if (type === 'applyEditDeltaComplete' && layoutId === requestId) {
          clearTimeout(timeout);
          worker.removeEventListener('message', handler);

          if (error) {
            reject(new Error(error));
          } else {
            // Update our tracked document version
            this.currentDocumentVersion = newVersion;
            resolve({
              affectedParagraphIds: affectedParagraphIds || [],
              deletedParagraphIds: deletedParagraphIds || [],
              newVersion: newVersion,
              boundariesChanged: boundariesChanged || false
            });
          }
        }
      };

      worker.addEventListener('message', handler);
      worker.postMessage({
        type: 'applyEditDelta',
        payload: {
          layoutId: requestId,
          delta
        }
      });
    });
  }

  // 🚀 GLYPH WARMING: Warm glyph paths for better performance
  async warmGlyphPathsBin(fontUid: string, gids: number[], sizePx: number): Promise<any> {
    if (!this.worker || !this.isReady) {
      throw new Error(WORKER_NOT_INITIALIZED_ERROR);
    }

    const requestId = generateRequestId(this.requestIdRef, 'warm');
    const worker = this.worker;

    return new Promise((resolve, reject) => {
      const timeout = setTimeout(() => {
        reject(new Error('Glyph warming timeout (5s)'));
      }, 5000);

      let glyphsReceived = 0;
      const expectedGlyphs = gids.length;

      const handler = (e: MessageEvent) => {
        const { type, payload } = e.data;

        if (type === 'glyphPathBin' && payload?.fontUid === fontUid && payload?.sizePx === sizePx && gids.includes(payload.gid)) {
          // Process individual glyph responses
          GlyphDataProcessor.processAndCacheGlyphData(payload, this.glyphCacheCallback);
          glyphsReceived++;

          DEBUG_ENABLED && console.log(`Received glyph ${payload.gid} (${glyphsReceived}/${expectedGlyphs}) for warming`);

          // If we've received all expected glyphs, resolve
          if (glyphsReceived >= expectedGlyphs) {
            clearTimeout(timeout);
            worker.removeEventListener('message', handler);
            resolve({ count: glyphsReceived, ok: true });
          }
        } else if (type === 'glyphPathsWarmed' && payload?.requestId === requestId) {
          clearTimeout(timeout);
          worker.removeEventListener('message', handler);

          if (payload.error) {
            reject(new Error(payload.error));
          } else {
            resolve(payload);
          }
        }
      };

      worker.addEventListener('message', handler);
      worker.postMessage({
        type: 'warmGlyphPathsBin',
        payload: {
          requestId,
          fontUid,
          gids,
          sizePx
        }
      });
    });
  }

  // 🚀 GLYPH LOADING: Get individual glyph path
  async getGlyphPathBin(fontUid: string, gid: number, sizePx: number): Promise<any> {
    if (!this.worker || !this.isReady) {
      throw new Error(WORKER_NOT_INITIALIZED_ERROR);
    }

    const requestId = generateRequestId(this.requestIdRef, 'glyph');
    const worker = this.worker;

    return new Promise((resolve, reject) => {
      const timeout = setTimeout(() => {
        reject(new Error('Glyph loading timeout (5s)'));
      }, 5000);

      const handler = (e: MessageEvent) => {
        const { type, payload } = e.data;

        if (type === 'glyphPathBin' && payload?.requestId === requestId) {
          clearTimeout(timeout);
          worker.removeEventListener('message', handler);

          if (payload.error) {
            reject(new Error(payload.error));
          } else {
            // Process and cache the glyph data
            GlyphDataProcessor.processAndCacheGlyphData(payload, this.glyphCacheCallback);
            resolve(payload);
          }
        }
      };

      worker.addEventListener('message', handler);
      worker.postMessage({
        type: 'getGlyphPathBin',
        payload: {
          requestId,
          fontUid,
          gid,
          sizePx
        }
      });
    });
  }

  // Set callback for when glyphs are cached (for triggering re-renders)
  setGlyphCacheCallback(callback: () => void) {
    this.glyphCacheCallback = callback;
  }


  // 🚀 INCREMENTAL LAYOUT: Layout only affected paragraphs
  async layoutAffectedParagraphs(paragraphIds: string[]): Promise<{
    layouts: any[],
    telemetry: any,
    hasErrors: boolean,
    unknownParagraphIds: string[],
    allParagraphIds: string[]
  }> {
    if (!this.worker || !this.isReady) {
      throw new Error(WORKER_NOT_INITIALIZED_ERROR);
    }

    const requestId = generateRequestId(this.requestIdRef, 'affected');
    const worker = this.worker;

    return new Promise((resolve, reject) => {
      const timeout = setTimeout(() => {
        reject(new Error('Affected paragraphs layout timeout (15s)'));
      }, 15000);

      const handler = (e: MessageEvent) => {
        const { type, payload } = e.data;

        if (type === 'layoutAffectedParagraphsComplete' && payload?.layoutId === requestId) {
          clearTimeout(timeout);
          worker.removeEventListener('message', handler);

          // 🆕 Phase 2 Observability: Forward WASM diagnostics to WeBelieveLogger
          forwardDiagnosticsToLogger(payload.diagnostics);

          if (payload.error) {
            reject(new Error(payload.error));
          } else {
            // ✅ layoutAffectedParagraphs returns snapshots, not pages
            // Convert snapshots to page-like format for cache compatibility
            DEBUG_ENABLED && console.log(`🔍 [TypesetterWorkerClient] layoutAffectedParagraphs response:`, {
              hasSnapshots: !!payload.snapshots,
              snapshotsLength: payload.snapshots?.length,
              snapshotsArray: payload.snapshots,
              hasPages: !!payload.pages,
              pagesLength: payload.pages?.length,
              pagesArray: payload.pages,
              payloadKeys: Object.keys(payload),
              fullPayload: payload
            });
            const layouts = payload.snapshots || payload.pages || [];
            DEBUG_ENABLED && console.log(`🔍 [TypesetterWorkerClient] Extracted layouts:`, layouts.length, layouts);
            resolve({
              layouts,
              telemetry: payload.telemetry,
              hasErrors: payload.hasErrors || false,
              unknownParagraphIds: payload.unknownParagraphIds || [],
              allParagraphIds: payload.allParagraphIds || []
            });
          }
        }
      };

      worker.addEventListener('message', handler);
      worker.postMessage({
        type: 'layoutAffectedParagraphs',
        payload: {
          layoutId: requestId,
          paragraphIds
        }
      });
    });
  }

  /**
   * Incremental pagination: Repaginate from SpillFrontier
   * Only processes pages from first affected paragraph forward
   * 
   * NEW for Phase 1: Enables incremental pagination with early termination
   * 
   * @param paragraphs All current paragraph snapshots
   * @param pageSetup Page setup (dimensions, margins)
   * @param spillFrontier Starting cursor (where to resume pagination)
   * @param maxPages Maximum pages to return (default: 100)
   * @param existingPageHashes Optional: existing page hashes for early termination
   * @returns PageWindowResult with pages, cursor, and telemetry
   */
  async paginateWindowFromFrontier(
    paragraphs: any[], // ParagraphSnapshot[]
    pageSetup: any, // PageSetup
    spillFrontier: { para_ix: number; line_ix: number },
    maxPages: number = 100,
    existingPageHashes?: Array<{ page_index: number; page_hash: string }>
  ): Promise<PageWindowResult> {
    if (!this.worker || !this.isReady) {
      throw new Error(WORKER_NOT_INITIALIZED_ERROR);
    }

    const requestId = generateRequestId(this.requestIdRef, 'paginate-window');
    const worker = this.worker;

    return new Promise((resolve, reject) => {
      const timeout = setTimeout(() => {
        reject(new Error('Paginate window timeout (30s)'));
      }, 30000);

      const handler = (e: MessageEvent) => {
        const { type, payload } = e.data;

        if (type === 'paginateWindowFromFrontierComplete' && payload?.layoutId === requestId) {
          clearTimeout(timeout);
          worker.removeEventListener('message', handler);

          if (payload.error) {
            reject(new Error(payload.error));
          } else {
            resolve({
              pages: payload.pages || [],
              next_cursor: payload.next_cursor,
              total_pages: payload.total_pages || 0,
              telemetry: payload.telemetry || {},
              snapshot: payload.snapshot,
              stopped_at_page: payload.stopped_at_page !== undefined ? payload.stopped_at_page : null
            });
          }
        }
      };

      worker.addEventListener('message', handler);
      worker.postMessage({
        type: 'paginateWindowFromFrontier',
        payload: {
          layoutId: requestId,
          paragraphs,
          pageSetup,
          opts: { line_numbers: false, columns: 1 },
          start: spillFrontier,
          maxPages,
          existingPageHashes: existingPageHashes || []
        }
      });
    });
  }

  /**
   * 🆕 PHASE 3A: Paginate from cached state (LIGHTWEIGHT)
   * 
   * This method does NOT send paragraph data to WASM. Instead, WASM reads
   * paragraphs from its internal DOCUMENT_CACHE. This dramatically reduces
   * data transfer for incremental pagination.
   * 
   * Prerequisites:
   * - initializeDocument must have been called (sets up page setup in cache)
   * - layoutAffectedParagraphs must have been called (populates paragraph snapshots in cache)
   * 
   * @param spillFrontier Starting cursor (where to resume pagination)
   * @param maxPages Maximum pages to return (default: 100)
   * @param useExistingHashes Whether to use cached page hashes for early termination (default: true)
   * @param startPageIndex Actual page index where pagination starts (for correct page_index in results)
   * @returns PageWindowResult with pages, cursor, and telemetry
   */
  async paginateFromCache(
    spillFrontier: { para_ix: number; line_ix: number },
    maxPages: number = 100,
    useExistingHashes: boolean = true,
    startPageIndex: number = 0  // 🆕 FIX: Correct page indices in results
  ): Promise<PageWindowResult & { paragraphs_used: number; affected_page_indices?: number[] }> {
    if (!this.worker || !this.isReady) {
      throw new Error(WORKER_NOT_INITIALIZED_ERROR);
    }

    const requestId = generateRequestId(this.requestIdRef, 'paginate-cache');
    const worker = this.worker;

    return new Promise((resolve, reject) => {
      const timeout = setTimeout(() => {
        reject(new Error('Paginate from cache timeout (30s)'));
      }, 30000);  // Increased timeout for large documents

      const handler = (e: MessageEvent) => {
        const { type, payload } = e.data;

        if (type === 'paginateFromCacheComplete' && payload?.layoutId === requestId) {
          clearTimeout(timeout);
          worker.removeEventListener('message', handler);

          // 🆕 Phase 2 Observability: Forward WASM diagnostics to WeBelieveLogger
          forwardDiagnosticsToLogger(payload.diagnostics);

          if (payload.error) {
            reject(new Error(payload.error));
          } else {
            resolve({
              pages: payload.pages || [],
              next_cursor: payload.next_cursor,
              total_pages: payload.total_pages || 0,
              telemetry: payload.telemetry || {},
              snapshot: payload.snapshot,
              stopped_at_page: payload.stopped_at_page !== undefined ? payload.stopped_at_page : null,
              paragraphs_used: payload.paragraphs_used || 0,
              affected_page_indices: payload.affected_page_indices  // 🆕 Only changed pages
            });
          }
        }
      };

      worker.addEventListener('message', handler);
      worker.postMessage({
        type: 'paginateFromCache',
        payload: {
          layoutId: requestId,
          start: spillFrontier,
          maxPages,
          useExistingHashes,
          startPageIndex  // 🆕 FIX: Pass to worker
        }
      });
    });
  }

  /**
   * 🆕 STREAMING PAGINATION: Pages delivered incrementally as computed
   * 
   * Unlike paginateFromCache which waits for all pages, this method
   * streams pages one at a time via callbacks. This provides:
   * - Faster time-to-first-page
   * - Progressive UI updates
   * - Ability to cancel mid-stream
   * 
   * @param spillFrontier Starting cursor
   * @param options Streaming options with callbacks
   * @returns Handle with cancel() and promise
   */
  paginateStreaming(
    spillFrontier: { para_ix: number; line_ix: number },
    options: {
      maxPages?: number;
      startPageIndex?: number;
      existingHashes?: Map<number, string>;
      onPage: (page: PageLayout, index: number, hashChanged: boolean) => void;
      onComplete: (result: { totalPages: number; pagesEmitted: number; durationMs: number }) => void;
      onError: (error: Error) => void;
    }
  ): { cancel: () => void; promise: Promise<void>; isActive: () => boolean } {
    const {
      maxPages = 100,
      startPageIndex = 0,
      existingHashes,
      onPage,
      onComplete,
      onError
    } = options;

    if (!this.worker || !this.isReady) {
      const error = new Error(WORKER_NOT_INITIALIZED_ERROR);
      onError(error);
      return {
        cancel: () => {},
        promise: Promise.reject(error),
        isActive: () => false
      };
    }

    const requestId = generateRequestId(this.requestIdRef, 'stream');
    const worker = this.worker;
    let cancelled = false;
    let completed = false;
    let resolver: () => void;
    let rejecter: (e: Error) => void;

    const promise = new Promise<void>((resolve, reject) => {
      resolver = resolve;
      rejecter = reject;
    });

    const handler = (e: MessageEvent) => {
      if (cancelled || completed) return;
      const { type, payload } = e.data;
      if (payload?.layoutId !== requestId) return;

      switch (type) {
        case 'paginateStreamPage':
          try {
            onPage(payload.page, payload.pageIndex, payload.hashChanged ?? false);
          } catch (callbackError) {
            console.error('[TypesetterWorkerClient] onPage callback error:', callbackError);
          }
          break;

        case 'paginateStreamComplete':
          completed = true;
          worker.removeEventListener('message', handler);
          try {
            onComplete({
              totalPages: payload.totalPages,
              pagesEmitted: payload.pagesEmitted,
              durationMs: payload.durationMs
            });
          } catch (callbackError) {
            console.error('[TypesetterWorkerClient] onComplete callback error:', callbackError);
          }
          resolver();
          break;

        case 'paginateStreamError':
          completed = true;
          worker.removeEventListener('message', handler);
          const error = new Error(payload.error || 'Streaming pagination failed');
          try {
            onError(error);
          } catch (callbackError) {
            console.error('[TypesetterWorkerClient] onError callback error:', callbackError);
          }
          rejecter(error);
          break;
      }
    };

    worker.addEventListener('message', handler);

    // Convert hash map to object for postMessage serialization
    const hashesObj: Record<number, string> = {};
    existingHashes?.forEach((hash, idx) => {
      hashesObj[idx] = hash;
    });

    worker.postMessage({
      type: 'paginateFromCacheStreaming',
      payload: {
        layoutId: requestId,
        start: spillFrontier,
        maxPages,
        startPageIndex,
        pageHashes: hashesObj
      }
    });

    return {
      cancel: () => {
        if (cancelled || completed) return;
        cancelled = true;
        worker.removeEventListener('message', handler);
        worker.postMessage({
          type: 'cancelStreamingPagination',
          payload: { layoutId: requestId }
        });
        resolver(); // Resolve (not reject) on cancel
      },
      promise,
      isActive: () => !cancelled && !completed
    };
  }

  // ═══════════════════════════════════════════════════════════════════════════════
  // PERSISTENT CACHE (IndexedDB) - L2 Cache Layer
  // ═══════════════════════════════════════════════════════════════════════════════

  /**
   * Enable persistent caching for a document.
   * Call this when opening a document to enable IndexedDB-backed caching.
   * 
   * @param documentId - Unique identifier for the document (e.g., UUID from backend)
   * @param contentHash - Hash of document content (changes on every edit)
   * @returns Promise that resolves when cache is ready
   */
  async enablePersistentCache(documentId: string, contentHash?: string): Promise<void> {
    if (!this.cacheOrchestrator) {
      this.cacheOrchestrator = getCacheOrchestrator();
    }
    
    this.currentDocumentId = documentId;
    const hash = contentHash || this.currentDocHash;
    
    if (hash) {
      this.cacheOrchestrator.setDocumentContext(documentId, hash);
      console.log(`[TypesetterWorkerClient] Persistent cache enabled for document ${documentId} with hash ${hash.substring(0, 8)}`);
    } else {
      console.log(`[TypesetterWorkerClient] Persistent cache enabled for document ${documentId} (hash will be set on first content load)`);
    }
  }

  /**
   * Compute content hash from text and font settings.
   * This can be called early (before initializeDocument) to enable cache lookups.
   * 
   * @param text - Document text
   * @param fontFamily - Font family name
   * @param fontWeight - Font weight
   * @param fontStyle - Font style
   * @returns Content hash string
   */
  computeContentHash(
    text: string,
    fontFamily?: string,
    fontWeight?: number,
    fontStyle?: string
  ): string {
    return computeDocHash(text, fontFamily, fontWeight, fontStyle);
  }

  /**
   * Check if the current document has cached pages in IndexedDB.
   * Call this before pagination to potentially skip WASM typesetting.
   * 
   * @returns Object with cache status and page count
   */
  async checkPersistentCache(contentHash?: string): Promise<{
    hasCache: boolean;
    pageCount: number;
    canSkipTypesetting: boolean;
  }> {
    // Use provided hash or fall back to current hash
    const hash = contentHash || this.currentDocHash;
    
    if (!this.cacheOrchestrator || !this.currentDocumentId) {
      return { hasCache: false, pageCount: 0, canSkipTypesetting: false };
    }
    
    if (!hash) {
      console.warn('[TypesetterWorkerClient] checkPersistentCache called without content hash');
      return { hasCache: false, pageCount: 0, canSkipTypesetting: false };
    }
    
    // Update context with the provided or current hash
    this.cacheOrchestrator.setDocumentContext(this.currentDocumentId, hash);
    // Also update currentDocHash so subsequent operations use the same hash
    if (contentHash) {
      this.currentDocHash = contentHash;
    }
    
    const hasCache = await this.cacheOrchestrator.hasPersistedDocument();
    const pageCount = hasCache ? await this.cacheOrchestrator.getPersistedPageCount() : 0;
    
    console.log(`[TypesetterWorkerClient] Cache check: hasCache=${hasCache}, pageCount=${pageCount}, hash=${hash.substring(0, 8)}`);
    
    return {
      hasCache,
      pageCount,
      // Can skip typesetting if we have at least 1 page cached
      canSkipTypesetting: pageCount >= 1
    };
  }

  /**
   * Load pages from persistent cache (IndexedDB) for fast document open.
   * Falls back to WASM pagination if cache miss.
   * 
   * @param pageIndices - Array of page indices to load
   * @returns Map of page index to PageLayout, or null for misses
   */
  async loadFromPersistentCache(pageIndices: number[]): Promise<{
    pages: Map<number, PageLayout>;
    hits: number;
    misses: number[];
    latencyMs: number;
  }> {
    if (!this.cacheOrchestrator) {
      return {
        pages: new Map(),
        hits: 0,
        misses: pageIndices,
        latencyMs: 0
      };
    }
    
    const result = await this.cacheOrchestrator.getPages(pageIndices);
    
    return {
      pages: result.pages,
      hits: result.l1Hits + result.l2Hits,
      misses: result.misses,
      latencyMs: result.latencyMs
    };
  }

  /**
   * Save pages to persistent cache after pagination.
   * Called automatically during pagination, but can be called manually.
   * 
   * @param pages - Pages to cache
   * @param contentHash - Optional content hash to use (overrides current context)
   */
  async saveToPersistentCache(pages: PageLayout[], contentHash?: string): Promise<void> {
    if (!this.cacheOrchestrator || pages.length === 0) return;
    
    // If explicit hash provided, update orchestrator context before saving
    // This ensures we save with the server's astHash, not the client-computed hash
    if (contentHash && this.currentDocumentId) {
      this.cacheOrchestrator.setDocumentContext(this.currentDocumentId, contentHash);
      console.log(`[TypesetterWorkerClient] Saving to cache with explicit hash: ${contentHash.substring(0, 8)}...`);
    }
    
    await this.cacheOrchestrator.setPages(pages);
  }

  /**
   * Get statistics about the cache layers.
   */
  async getCacheStatistics(): Promise<{
    l1Size: number;
    l2TotalEntries: number;
    l2UniqueDocuments: number;
    sessionHitRate: number;
  } | null> {
    if (!this.cacheOrchestrator) return null;
    
    const stats = await this.cacheOrchestrator.getStats();
    return {
      l1Size: stats.l1.size,
      l2TotalEntries: stats.l2.totalEntries,
      l2UniqueDocuments: stats.l2.uniqueDocuments,
      sessionHitRate: stats.l1.hitRate
    };
  }

  /**
   * Clear all persistent cache data.
   * Use with caution - clears cache for ALL documents.
   */
  async clearPersistentCache(): Promise<void> {
    if (this.cacheOrchestrator) {
      await this.cacheOrchestrator.clear();
      console.log('[TypesetterWorkerClient] Persistent cache cleared');
    }
  }

  // ═══════════════════════════════════════════════════════════════════════════════

  async layoutDocumentPaginated(input: DocumentLayoutInput): Promise<PageWindowResult> {
    if (!this.worker || !this.isReady) {
      throw new Error(WORKER_NOT_INITIALIZED_ERROR);
    }
    
    const requestId = generateRequestId(this.requestIdRef, 'paginated');
    const worker = this.worker; // Capture worker reference for type safety
    
    return this.enqueue(() => new Promise((resolve, reject) => {
      const timeout = setTimeout(() => {
        reject(new Error('Pagination request timeout (30s)'));
      }, 30000); // 30s timeout
      
      const handler = (e: MessageEvent) => {
        const { type, payload } = e.data;
        
        if (type === 'layout-paginated-result' && payload?.id === requestId) {
          clearTimeout(timeout);
          worker.removeEventListener('message', handler);
          // ✅ Attach caret_map from snapshot into each page for consumers that expect it on pages
          try {
            const result = payload.result as any;
            const caretMap = result?.snapshot?.caret_map;
            if (Array.isArray(result?.pages) && caretMap) {
              result.pages = result.pages.map((p: any) => ({ ...p, caret_map: caretMap }));
            }
            // Cache latest snapshot for fallbacks
            if (result?.snapshot) {
              this.lastLayoutResult = result.snapshot;
            }
            resolve(result);
          } catch (err) {
            // If augmentation fails, return original result
            resolve(payload.result);
          }
        } else if (type === 'layout-paginated-error' && payload?.id === requestId) {
          clearTimeout(timeout);
          worker.removeEventListener('message', handler);
          reject(new Error(payload.error));
        }
      };
      
      worker.addEventListener('message', handler);
      
      // ✅ CRITICAL FIX: Use Transferable objects for font bytes to avoid JSON corruption
      // Extract ArrayBuffers from font bytes and pass as Transferable objects
      // This prevents JSON.stringify from corrupting the JSON structure
      const { inputWithoutBytes, fontByteIndices, fontBuffers, totalBytesTransferred } = prepareFontsAsTransferable(input);
      
      // Create message payload with Transferable font buffers
      const messagePayload = createTransferableMessagePayload(requestId, inputWithoutBytes, fontByteIndices, fontBuffers);
      
      // Send with Transferable objects - ArrayBuffers are moved to worker, not copied
      if (fontBuffers.length > 0) {
        worker.postMessage(messagePayload, fontBuffers);
        DEBUG_ENABLED && console.log(`✅ [TypesetterWorkerClient] Sent ${fontBuffers.length} font ArrayBuffers as Transferable objects (${(totalBytesTransferred / 1024).toFixed(2)}KB total)`);
      } else {
        worker.postMessage(messagePayload);
      }
    }));
  }
  
  /**
   * 🆕 NEW for Phase 2: Get visible pages with caching (high-level)
   * 
   * This is the main API for pagination. It handles:
   * - Document change detection (via hash)
   * - Page caching (only requests missing pages)
   * - Lookahead prefetching (request extra pages ahead)
   * 
   * @param documentText - Full document text
   * @param pageSetup - Page dimensions and margins
   * @param visiblePageRange - [startPage, endPage] to return
   * @param lookahead - Number of extra pages to prefetch ahead
   * @returns Object with pages array and full document snapshot (includes caret_map)
   */
  async getVisiblePages(
    documentText: string,
    pageSetup: PageSetup,
    visiblePageRange: [number, number],
    fontFamily: string = 'Times New Roman',
    fontSizePx: number = 16,
    lookahead: number = 2,
    fontWeight: number = 400,      // ✅ NEW: Font weight for cache key
    fontStyle: 'normal' | 'italic' | 'oblique' = 'normal' // ✅ NEW: Font style for cache key
  ): Promise<PageWindowResult> {
    // 🔥 CIRCUIT BREAKER: Stop processing if WASM has crashed
    checkWasmCrashed(this.wasmCrashed);
    
    const [startPage, endPage] = visiblePageRange;
    // ✅ HARD GUARD: Require DB-provided font family before pagination
    if (!fontFamily) {
      return createFallbackPageWindowResult(this.getLastLayoutResult(), 0);
    }
    
    // ✅ CRITICAL FIX: Validate pageSetup structure before accessing properties
    validatePageSetupOrThrow(pageSetup);
    // ✅ UPDATED: Include font information in cache key
    const docHash = computeDocHash(documentText, fontFamily, fontWeight, fontStyle);
    
    // Update current state
    this.currentDocumentText = documentText;
    this.currentPageSetup = pageSetup;
    
    // Check if document changed (invalidate cache)
    const docChange = detectDocumentHashChange(docHash, this.currentDocHash);
    if (docChange.hasChanged) {
      DEBUG_ENABLED && console.log('📄 [TypesetterWorkerClient] Document changed, invalidating all caches');
      this.pageCache.clear();
      this.currentDocHash = docHash;
      // ✅ CRITICAL: Clear stale lastLayoutResult to prevent fallback to old document
      this.lastLayoutResult = null;
      
      // 🆕 L2 PERSISTENT CACHE: Update context when hash changes
      if (this.cacheOrchestrator && this.currentDocumentId) {
        this.cacheOrchestrator.setDocumentContext(this.currentDocumentId, docHash);
      }
      
      // ✅ CRITICAL FIX: Also clear all related caches for consistency with applyIncrementalTextChange
      // This ensures complete cache invalidation when document hash changes
      this.pageHashes.clear();
      
      // 🔥 CRITICAL FIX: Do NOT clear cachedParagraphSnapshots if we have proper per-paragraph snapshots.
      // storeInitialParagraphSnapshots() may have already stored good snapshots during document init.
      // Clearing them here would destroy the multi-paragraph data, causing the "7 lines instead of 4" bug.
      const hasProperPerParagraphSnapshots = this.cachedParagraphSnapshots.length > 1;
      if (!hasProperPerParagraphSnapshots) {
        this.cachedParagraphSnapshots.length = 0;
        DEBUG_ENABLED && console.log('🗑️ [TypesetterWorkerClient] Cleared paragraph snapshots (none or single para-0)');
      } else {
        console.log(`📊 [TypesetterWorkerClient] Preserving ${this.cachedParagraphSnapshots.length} per-paragraph snapshots (not clearing on hash change)`);
      }
      
      this.paragraphToPageMap.clear();
      DEBUG_ENABLED && console.log('🗑️ [TypesetterWorkerClient] Cleared page hashes and paragraph-to-page mapping');
    }

    // Note: removed time-based backoff to avoid masking recovery; rely on one-time reinit below
    
    // Check cache for visible pages
    const { cached: cachedPages, missing: missingPages } = checkCacheForRange(
      this.pageCache,
      startPage,
      endPage,
      lookahead,
      (p) => generatePageCacheKey(docHash, p)
    );
    
    // If all pages cached, we still need the snapshot (call layoutDocument to get it)
    // TODO: Cache snapshot separately to avoid this call
    if (missingPages.length === 0) {
      DEBUG_ENABLED && console.log(`⚠️ [TypesetterWorkerClient] Pages cached but need snapshot - calling layoutDocumentPaginated`);
    }
    
    DEBUG_ENABLED && console.log(`📥 [TypesetterWorkerClient] Requesting pages ${startPage}-${endPage + lookahead} (${missingPages.length} missing)`);
    DEBUG_ENABLED && console.log(`📥 [TypesetterWorkerClient] Document text length: ${documentText.length} chars`);
    
    // Prepare fonts for pagination using the coordinator utility
    const fontPreparation = await FontPreparationCoordinator.prepareFontsForPagination(
      fontFamily,
      fontWeight,
      fontStyle,
      fontSizePx
    );

    const { fontMetadata, fontsWithTransferableBytes } = fontPreparation;
    const { wasmFontUid, wasmFontFamily, wasmPostscriptName } = fontMetadata;

    try {
      // 🚀 OPTIMIZATION: Check page cache first to avoid unnecessary WASM calls
    const neededPages = findMissingPageIndices(
      this.pageCache,
      startPage,
      endPage,
      lookahead,
      (pageIdx) => generatePageCacheKey(docHash, pageIdx)
    );

    // 🚀 OPTIMIZATION: Background pagination - prefetch additional pages when user is idle
    // This prevents jank when scrolling to uncached pages
    if (neededPages.length === 0) {
      const prefetchRange = calculatePrefetchRange({
        endPage,
        lookahead,
        maxPages: 100,
        prefetchCount: 2
      });

      if (prefetchRange.shouldPrefetch && prefetchRange.prefetchStart !== null && prefetchRange.prefetchEnd !== null) {
        // Only prefetch if not already in flight
        const prefetchKey = generatePrefetchRangeKey(
          docHash,
          prefetchRange.prefetchStart,
          prefetchRange.prefetchEnd,
          wasmFontUid,
          fontSizePx,
          generatePaginationRangeKey
        );

        if (!isRequestInFlight(this.inFlightPaginations, prefetchKey)) {
          DEBUG_ENABLED && console.log(`🚀 [TypesetterWorkerClient] Background prefetching pages ${prefetchRange.prefetchStart}-${prefetchRange.prefetchEnd}`);
          // Fire and forget - don't await
          // TODO: Implement proper prefetching with correct parameters
          // this.getVisiblePages(documentText, pageSetup, [prefetchRange.prefetchStart, prefetchRange.prefetchEnd], fontFamily, fontSizePx, 0, fontWeight, fontStyle)
          //   .catch(err => DEBUG_ENABLED && console.warn('Background prefetch failed:', err));
        }
      }
    }

    // If all pages are cached, return immediately
    if (neededPages.length === 0) {
      DEBUG_ENABLED && console.log('[TypesetterWorkerClient] All pages cached, returning immediately');
      const cachedPages = collectCachedPagesInRange(
        this.pageCache,
        startPage,
        endPage,
        (pageIdx) => generatePageCacheKey(docHash, pageIdx)
      );
      return createCachedPageWindowResult(cachedPages, this.lastLayoutResult!);
    }

    // Request missing pages from WASM (single-paragraph approach)
    const rangeKey = generatePaginationRangeKey(docHash, startPage, endPage + lookahead, wasmFontUid, fontSizePx);
    const existing = getInFlightRequest(this.inFlightPaginations, rangeKey);
    if (existing) {
      DEBUG_ENABLED && console.log('[TypesetterWorkerClient] Reusing in-flight pagination', rangeKey);
      const result = await existing;
      // Cache and slice to visible
      cachePages(this.pageCache, docHash, result.pages);
      const visiblePages = filterAndSortPagesByRange(result.pages, startPage, endPage);
      return result; // Return full PageWindowResult from WASM
    }

    // Build pagination input using extracted utility
    const input = buildPaginationInput({
      documentText,
      pageSetup,
      wasmFontUid,
      wasmPostscriptName,
      wasmFontFamily,
      fontWeight,
      fontStyle,
      fontSizePx,
      fontsWithTransferableBytes,
      neededPages,
      startPage,
      endPage,
      lookahead
    });

    const paginationPromise = this.layoutDocumentPaginated(input as any);
    setInFlightRequest(this.inFlightPaginations, rangeKey, paginationPromise);
    const result = await paginationPromise.finally(() => removeInFlightRequest(this.inFlightPaginations, rangeKey));

    // Process pagination result and update all caches/state
    const processingState = {
      pageCache: this.pageCache,
      pageHashes: this.pageHashes,
      paragraphToPageMap: this.paragraphToPageMap,
      cachedParagraphSnapshots: this.cachedParagraphSnapshots,
      currentTotalPages: this.currentTotalPages,
      consecutiveWasmCrashes: this.consecutiveWasmCrashes
    };

    const visiblePages = processPaginationResult(
      {
        result,
        docHash,
        startPage,
        endPage,
        consecutiveWasmCrashes: this.consecutiveWasmCrashes
      },
      processingState
    );

    // Update instance state with processed values
    this.consecutiveWasmCrashes = processingState.consecutiveWasmCrashes;
    this.currentTotalPages = processingState.currentTotalPages;  // 🔥 FIX: Update total pages from pagination result

    return result; // Return full PageWindowResult from WASM
    } catch (e) {
      const err = e as Error;
      console.error('❌ [TypesetterWorkerClient] Pagination failed during font preload or layout:', err?.message || e);
      
      // 🔥 CIRCUIT BREAKER: Track consecutive crashes
      this.consecutiveWasmCrashes += 1;
      
      const circuitBreakerCheck = checkCircuitBreaker({
        consecutiveFailures: this.consecutiveWasmCrashes,
        maxConsecutiveFailures: this.MAX_CONSECUTIVE_CRASHES
      });
      
      if (circuitBreakerCheck.shouldTrigger) {
        logCircuitBreakerTrigger(this.consecutiveWasmCrashes);
        this.wasmCrashed = true;
        throw new Error(circuitBreakerCheck.errorMessage);
      }
      
      // Check for any WASM-related error (memory, unreachable, C++ exception)
      if (isWasmError(err)) {
        trackPaginationFault(this.lastPaginationFaultAtByDoc, docHash, { value: this.lastPaginationFaultAtMs });
        // Try one-time reinitialization to recover worker state
        if (shouldAttemptReinitialization({
          didReinitAfterFault: this.didReinitAfterFault,
          hasPageSetup: !!this.currentPageSetup
        })) {
          try {
            // Hard reset worker for clean memory state (radical/high-reward)
            try { this.terminate(); } catch {}
            await this.initialize();
            const fontMetadata = resolveFontMetadata(fontFamily, fontWeight, fontStyle);
            const { wasmFontUid } = fontMetadata;
            
            // Convert internal page setup back to input format for reinitialization
            const pageSetupInput = PageSetupConverter.toInitializeDocumentFormat(this.currentPageSetup);
            
            await this.initializeDocument({
              text: this.currentDocumentText,
              paragraphs: [{ id: 'para-0', type: 'p', graphemeStart: 0, graphemeEnd: this.currentDocumentText.length, version: 1 }],
              pageSetup: {
                pageSize: typeof pageSetupInput.pageSize === 'object' ? pageSetupInput.pageSize : { width: 8.5, height: 11 },
                orientation: pageSetupInput.orientation,
                marginsTop: pageSetupInput.marginsTop,
                marginsRight: pageSetupInput.marginsRight,
                marginsBottom: pageSetupInput.marginsBottom,
                marginsLeft: pageSetupInput.marginsLeft,
                columns: pageSetupInput.columns,
                contentRectXFp: pageSetupInput.contentRectXFp,
                contentRectYFp: pageSetupInput.contentRectYFp,
                contentRectWFp: pageSetupInput.contentRectWFp,
                contentRectHFp: pageSetupInput.contentRectHFp
              },
              fonts: [{ fontUid: wasmFontUid, family: fontFamily, weight: fontWeight, style: fontStyle }],
              defaultFontSizePx: fontSizePx
            }, true);
            this.didReinitAfterFault = true;
            // Retry once after reinit
            return await this.getVisiblePages(documentText, pageSetup, visiblePageRange, fontFamily, fontSizePx, lookahead, fontWeight, fontStyle);
          } catch (re) {
            logReinitializationFailure(re as Error);
          }
        }
        // Fallback: return last known snapshot; let UI keep rendering without spamming retries
        const fallbackSnap = this.getLastLayoutResult();
        return createFallbackPageWindowResult(fallbackSnap, 0);
      }
      throw err;
    }
  }
  
  /**
   * 🆕 NEW for Phase 2: Invalidate pages after edit
   * 
   * @param startPage - Page to start invalidation from (undefined = invalidate all)
   */
  invalidatePages(startPage?: number): void {
    if (startPage === undefined) {
      DEBUG_ENABLED && console.log('🗑️ [TypesetterWorkerClient] Invalidating ALL pages');
      this.pageCache.clear();
    } else {
      DEBUG_ENABLED && console.log(`🗑️ [TypesetterWorkerClient] Invalidating pages from ${startPage} onwards`);
      const docHash = this.currentDocHash;
      // Remove all pages >= startPage
      Array.from(this.pageCache.keys())
        .filter(key => matchesCacheKeyCriteria(key, docHash, startPage))
        .forEach(key => this.pageCache.delete(key));
    }
  }
  
  /**
   * 🆕 NEW for Phase 2: Get cache statistics (for debugging)
   */
  getCacheStats(): { size: number; docHash: string; pages: number[] } {
    return CacheStatisticsCalculator.getCacheStats(this.pageCache, this.currentDocHash);
  }
  
  // ============================================================================
  // 🆕 PHASE 2: SpillFrontier and Incremental Pagination Helpers
  // ============================================================================
  
  /**
   * 🆕 PHASE 2: Build paragraph-to-page mapping from page layouts
   * 
   * Called after pagination to track which pages contain which paragraphs.
   * Used for SpillFrontier calculation.
   * 
   * @param pages - Array of PageLayout from pagination result
   */
  
  /**
   * 🆕 PHASE 2: Store page hashes for early termination
   * 
   * Called after pagination to store hashes for hash comparison
   * during subsequent incremental pagination.
   * 
   * @param pages - Array of PageLayout from pagination result
   */
  
  /**
   * 🆕 PHASE 2: Get existing page hashes for early termination
   * 
   * Returns array of {page_index, page_hash} for paginateWindowFromFrontier
   */
  getExistingPageHashes(): Array<{ page_index: number; page_hash: string }> {
    return pageHashesToArray(this.pageHashes);
  }
  
  /**
   * 🆕 PHASE 2: Update paragraph snapshot cache
   * 
   * Merges new snapshots into the cached array.
   * Called after layoutAffectedParagraphs to keep cache in sync.
   * 
   * @param newSnapshots - Array of ParagraphSnapshot from incremental layout
   */
  
  /**
   * 🔧 CRITICAL FIX: Store initial paragraph snapshots on document load.
   * 
   * After initializeDocument + layoutAffectedParagraphs, the frontend has per-paragraph
   * snapshots. These MUST be stored in cachedParagraphSnapshots so that:
   * 1. Canvas can extract lines by paragraph slice (paragraph_id matching)
   * 2. Incremental edits can merge with existing snapshots
   * 
   * Without this, getVisiblePages only stores ONE combined snapshot with ID 'para-0',
   * causing duplicate line ranges when rendering multi-paragraph documents.
   * 
   * @param layouts - Array of layout results from layoutAffectedParagraphs
   */
  storeInitialParagraphSnapshots(layouts: any[]): void {
    const snapshots = extractParagraphSnapshots(layouts);
    
    if (snapshots.length === 0) {
      console.warn('⚠️ [TypesetterWorkerClient] storeInitialParagraphSnapshots: No snapshots extracted');
      return;
    }
    
    // Clear existing and store fresh snapshots
    this.cachedParagraphSnapshots.length = 0;
    this.cachedParagraphSnapshots.push(...snapshots);
    
    console.log(`✅ [TypesetterWorkerClient] Stored ${snapshots.length} initial paragraph snapshots`, {
      paragraphIds: snapshots.map(s => s.paragraph_id || s.snapshot_id),
      linesPerParagraph: snapshots.map(s => s.lines?.length || 0)
    });
  }
  
  /**
   * 🆕 PHASE 2: Get all cached paragraph snapshots
   * 
   * Returns the client-side cached snapshots for incremental pagination.
   * These are needed because paginateWindowFromFrontier requires all paragraphs.
   * 
   * 🔥 FIX (2026-01-13): Sanitize cache before returning to prevent stale lines
   * from causing visual corruption. This is the LAST LINE OF DEFENSE before render.
   */
  getCachedParagraphSnapshots(): ParagraphSnapshot[] {
    // � DEBUG: Log every call to trace the flow
    const committedLen = this.lastKnownDocumentLength;
    console.log(`🔬 [getCachedParagraphSnapshots] Called at ${Date.now()} with lastKnownDocumentLength=${committedLen}, cache has ${this.cachedParagraphSnapshots.length} snapshots`);
    
    // 🔬 DEBUG: Log every line in the cache BEFORE sanitization
    let totalLines = 0;
    let staleLinesBefore = 0;
    for (const snapshot of this.cachedParagraphSnapshots) {
      if (snapshot.lines) {
        for (const line of snapshot.lines) {
          totalLines++;
          if (line.end_grapheme_index > committedLen) {
            staleLinesBefore++;
            console.warn(`🔬 [getCachedParagraphSnapshots] PRE-SANITIZE stale line: [${line.start_grapheme_index}-${line.end_grapheme_index}) > committedLen=${committedLen}`);
          }
        }
      }
    }
    console.log(`🔬 [getCachedParagraphSnapshots] PRE-SANITIZE: ${totalLines} total lines, ${staleLinesBefore} stale`);
    
    // 🔥 SANITIZATION: Filter out stale lines that exceed last known document length
    // This prevents visual corruption when DELETE operations shrink the document
    // but old cached lines persist with outdated grapheme indices.
    if (committedLen > 0) {
      let totalStaleFiltered = 0;
      for (const snapshot of this.cachedParagraphSnapshots) {
        if (snapshot.lines) {
          const originalLen = snapshot.lines.length;
          snapshot.lines = snapshot.lines.filter(line => {
            const isValid = line.end_grapheme_index <= committedLen;
            if (!isValid) {
              totalStaleFiltered++;
            }
            return isValid;
          });
          if (snapshot.lines.length < originalLen) {
            console.log(`🗑️ [getCachedParagraphSnapshots] Sanitized snapshot: removed ${originalLen - snapshot.lines.length} stale lines (end > ${committedLen})`);
          }
        }
      }
      if (totalStaleFiltered > 0) {
        console.warn(`🗑️ [getCachedParagraphSnapshots] SANITIZATION: Removed ${totalStaleFiltered} stale lines with grapheme indices > committedLen=${committedLen}`);
      }
    }
    
    // 🔬 DEBUG: Log state AFTER sanitization
    let totalLinesAfter = 0;
    for (const snapshot of this.cachedParagraphSnapshots) {
      if (snapshot.lines) {
        totalLinesAfter += snapshot.lines.length;
      }
    }
    console.log(`🔬 [getCachedParagraphSnapshots] POST-SANITIZE: ${totalLinesAfter} total lines`);
    
    return this.cachedParagraphSnapshots;
  }
  
  /**
   * 🔥 Track last known document length for sanitization.
   * Updated during applyIncrementalTextChange.
   */
  private lastKnownDocumentLength: number = 0;
  
  /**
   * 🆕 Get document paragraph metadata for save operations.
   * Returns paragraph boundaries and IDs needed for AST conversion with stable IDs.
   * 
   * 🔧 CRITICAL FIX (Phase 2): Derive paragraphs from cachedParagraphSnapshots.
   * documentParagraphs is only set during initializeDocument and is NOT updated
   * when user types and creates new paragraphs with ENTER. The snapshots are
   * always kept up-to-date after every edit via applyIncrementalTextChange.
   * 
   * For save operations, we need the CURRENT paragraph boundaries, which are
   * only available in the cached snapshots.
   */
  getDocumentParagraphs(): Array<{
    id: string;
    type: string;
    version: number;
    textStart: number;
    textEnd: number;
  }> {
    console.log('🔬 [TypesetterWorkerClient] getDocumentParagraphs called:', {
      documentParagraphsCount: this.documentParagraphs.length,
      cachedSnapshotCount: this.cachedParagraphSnapshots.length
    });
    
    // 🔧 FIX: ALWAYS derive from snapshots since they're kept up-to-date
    // documentParagraphs is only set on initial load and becomes stale after typing
    if (this.cachedParagraphSnapshots.length > 0) {
      const result = this.cachedParagraphSnapshots.map((snapshot, idx) => {
        const lines = snapshot.lines || [];
        const textStart = lines.length > 0 ? (lines[0].start_grapheme_index ?? 0) : 0;
        let textEnd = lines.length > 0 ? (lines[lines.length - 1].end_grapheme_index ?? 0) : 0;
        
        // 🔧 FIX: Exclude trailing \n\n from textEnd to get clean paragraph boundaries
        // WASM's line.end_grapheme_index includes the trailing newlines
        // For proper AST conversion, we need just the content boundaries
        const lastLine = lines[lines.length - 1];
        if (lastLine && lastLine.ends_with_newline) {
          // Subtract 2 for \n\n paragraph delimiter (not just 1)
          // Check if this is the last paragraph (no trailing newlines)
          const isLastParagraph = idx === this.cachedParagraphSnapshots.length - 1;
          if (!isLastParagraph) {
            textEnd = Math.max(textStart, textEnd - 2);
          }
        }
        
        const paraId = snapshot.paragraph_id || `para-${textStart}`;
        
        console.log(`🔬 [TypesetterWorkerClient] Paragraph ${idx} from snapshot:`, {
          id: paraId,
          textStart,
          textEnd,
          lineCount: lines.length,
          endsWithNewline: lastLine?.ends_with_newline
        });
        
        return {
          id: paraId,
          type: 'text',
          version: 1,
          textStart,
          textEnd
        };
      });
      
      console.log('🔬 [TypesetterWorkerClient] Returning paragraphs from snapshots:', result);
      return result;
    }
    
    // Fallback: use documentParagraphs if no snapshots (shouldn't happen)
    if (this.documentParagraphs.length > 0) {
      console.warn('⚠️ [TypesetterWorkerClient] No snapshots, falling back to documentParagraphs');
      return this.documentParagraphs.map((p, idx) => ({
        id: p.id,
        type: 'text',
        version: p.version,
        textStart: p.graphemeStart,
        textEnd: p.graphemeEnd
      }));
    }
    
    return [];
  }
  
  /**
   * 🆕 Get all lines from all cached paragraph snapshots.
   * Used for navigation when the current page's lines don't contain the cursor position.
   * Returns lines sorted by start_grapheme_index.
   */
  getAllCachedLines(): Line[] {
    const allLines: Line[] = [];
    for (const snapshot of this.cachedParagraphSnapshots) {
      if (snapshot.lines && snapshot.lines.length > 0) {
        allLines.push(...snapshot.lines);
      }
    }
    // Sort by start grapheme index to ensure proper order
    allLines.sort((a, b) => a.start_grapheme_index - b.start_grapheme_index);
    return allLines;
  }
  
  /**
   * 🆕 Get all caret positions from all cached paragraph snapshots.
   * Used for navigation when the current page's caret map doesn't contain the cursor position.
   * Returns caret positions sorted by grapheme_index.
   */
  getAllCachedCaretPositions(): CaretPosition[] {
    const allCarets: CaretPosition[] = [];
    for (const snapshot of this.cachedParagraphSnapshots) {
      if (snapshot.caret_map && snapshot.caret_map.length > 0) {
        allCarets.push(...snapshot.caret_map);
      }
    }
    // Sort by grapheme index
    allCarets.sort((a, b) => a.grapheme_index - b.grapheme_index);
    return allCarets;
  }
  
  /**
   * 🆕 PHASE 2: Get current total page count
   */
  getTotalPages(): number {
    return this.currentTotalPages;
  }
  
  
  // ============================================================================
  // 🆕 Phase 3.7: Pure WASM Architecture Methods
  // ============================================================================
  
  /**
   * Phase 3.3: Initialize document in WASM
   * 
   * Sends full document state to WASM for the first time.
   * Call this once on document load or worker restart.
   */
  async initializeDocument(options: {
    text: string;
    paragraphs: Array<{
      id: string;
      type: string;
      graphemeStart: number;
      graphemeEnd: number;
      version: number;
    }>;
    pageSetup: {
      pageSize: { width: number; height: number };
      orientation: 'portrait' | 'landscape';
      marginsTop: number;
      marginsRight: number;
      marginsBottom: number;
      marginsLeft: number;
      columns: number;
      contentRectXFp: number;
      contentRectYFp: number;
      contentRectWFp: number;
      contentRectHFp: number;
    };
    fonts: Array<{
      fontUid: string;
      family: string;
      weight: number;
      style: string;
    }>;
    defaultFontSizePx?: number; // Phase 3.4+: Default font size for document
  }, forceReinitialize: boolean = false): Promise<{ version: number }> {
    if (!this.worker || !this.isReady) {
      throw new Error('[TypesetterWorkerClient] Worker not initialized');
    }
    
    // If init is already in-flight, wait for it to complete
    if (this.initializeInFlight && !forceReinitialize) {
      DEBUG_ENABLED && console.log('[TypesetterWorkerClient] initializeDocument already in-flight, waiting...');
      return this.initializeInFlight;
    }
    
    // ✅ FIX: Allow re-initialization when transitioning from empty to non-empty text
    // This handles the race condition where document is initialized with empty text before AST loads
    const initState = detectInitializationState(
      this.lastInitializedText.length,
      options.text.length,
      this.documentInitialized,
      forceReinitialize
    );
    
    // Skip if already initialized (unless forced or transitioning from empty to non-empty)
    // After first init, ALL text changes must go through applyEditDelta
    if (shouldSkipInitialization(initState)) {
      DEBUG_ENABLED && console.log('[TypesetterWorkerClient] Document already initialized, skipping (text changes via delta only)');
      return Promise.resolve({ version: this.currentDocumentVersion });
    }
    
    // ✅ FIX: If transitioning from empty to non-empty, treat as forced re-initialization
    if (initState.isEmptyToNonEmptyTransition) {
      DEBUG_ENABLED && console.log('[TypesetterWorkerClient] Empty → non-empty transition detected, forcing re-initialization', {
        previousTextLength: this.lastInitializedText.length,
        newTextLength: options.text.length
      });
      forceReinitialize = true;
    }
    
    if (forceReinitialize) {
      DEBUG_ENABLED && console.log('[TypesetterWorkerClient] Forcing document reinitialization');
      // Clear caches/state so subsequent calls rehydrate cleanly
      resetStateForReinitialization({
        pageCache: this.pageCache,
        sentFonts: this.sentFonts,
        currentDocHash: this.currentDocHash
      });
      this.documentInitialized = false;
      this.initializeInFlight = null;
      // 🆕 PHASE 2: Clear Phase 2 caches on reinitialize
      this.currentTotalPages = Phase2CacheClearer.clearPhase2Caches(
        this.cachedParagraphSnapshots,
        this.paragraphToPageMap,
        this.pageHashes
      );
    }
    
    const layoutId = this.nextLayoutId++;
    const startTime = Date.now();
    
    DEBUG_ENABLED && console.log(`[TypesetterWorkerClient] 🆕 initializeDocument (layoutId=${layoutId}, text=${options.text.length} chars, ${options.paragraphs.length} paragraphs)`);

    // Convert paragraph ranges from grapheme indices to UTF-16 code unit indices
    const paragraphsUtf16 = convertParagraphRangesToUtf16(options.text, options.paragraphs);
    
    // Preload active fonts and attach bytes so WASM can shape with real faces immediately
    const fontsWithBytes = await prepareFontsWithBytes(options.fonts);
    
    // ✅ CRITICAL FIX: Create and store the promise BEFORE sending the message to worker
    // This ensures that any concurrent applyIncrementalTextChange calls will wait for
    // this initialization to complete, preventing "No paragraph found at position X" errors.
    // 
    // The previous code set initializeInFlight AFTER the Promise constructor, which meant
    // the postMessage was already sent but applyIncrementalTextChange couldn't see it was in-flight.
    
    let resolveInit: (result: { version: number }) => void;
    let rejectInit: (error: any) => void;
    
    const initPromise = new Promise<{ version: number }>((resolve, reject) => {
      resolveInit = resolve;
      rejectInit = reject;
    });
    
    // ✅ Set initializeInFlight BEFORE any async work (postMessage)
    this.initializeInFlight = initPromise;
    
    const timeout = setTimeout(() => {
      this.reactiveRequests.delete(layoutId);
      this.initializeInFlight = null;
      rejectInit(new Error('initializeDocument timeout'));
    }, 10000); // 10 second timeout
    
    this.reactiveRequests.set(layoutId, {
      resolve: (result: any) => {
        this.initializeInFlight = null;
        this.documentInitialized = true;
        this.lastInitializedText = options.text;
        this.currentDocumentVersion = result.version || 1;
        // 🔥 FIX (2026-01-13): Track document length for cache sanitization
        this.lastKnownDocumentLength = options.text.length;
        // 🆕 PHASE 2: Store paragraph metadata for SpillFrontier calculation
        this.documentParagraphs = options.paragraphs.map((p, idx) => ({
          id: p.id,
          graphemeStart: p.graphemeStart,
          graphemeEnd: p.graphemeEnd,
          version: p.version
        }));
        DEBUG_ENABLED && console.log(`📊 [Phase2] Stored ${this.documentParagraphs.length} paragraph metadata entries`);
        
        // 🆕 PHASE 3A FIX: Store page setup during initialization
        // This ensures currentPageSetup is available for incremental pagination
        // BEFORE getVisiblePages is called
        this.currentPageSetup = PageSetupConverter.fromInitializeDocumentFormat(options.pageSetup);
        DEBUG_ENABLED && console.log(`📊 [Phase3A] Stored page setup during init:`, {
          contentWidth: this.currentPageSetup.content_rect_fp.w_fp,
          contentHeight: this.currentPageSetup.content_rect_fp.h_fp,
          margins: this.currentPageSetup.margins_fp
        });
        
        clearTimeout(timeout);
        resolveInit(result);
      },
      reject: (error: any) => {
        this.initializeInFlight = null;
        clearTimeout(timeout);
        rejectInit(error);
      },
      timeout,
      startTime,
      operation: 'initializeDocument'
    });
    
    DEBUG_ENABLED && console.log(`[TypesetterWorkerClient] 📤 Sending initializeDocument with ${fontsWithBytes.length} fonts:`, fontsWithBytes.map(f => `${f.fontUid} (${f.bytes.length} bytes)`));

    this.worker!.postMessage({
      type: 'initializeDocument',
      layoutId,
      text: options.text,
      paragraphs: paragraphsUtf16,
      pageSetup: options.pageSetup,
      fonts: fontsWithBytes,
      defaultFontSizePx: options.defaultFontSizePx || 18 // Phase 3.4+: Default to 18px if not provided
    });
    
    return initPromise;
  }
  
  
  
  /**
   * Handle worker messages
   */
  private handleMessage(event: MessageEvent): void {
    const { type, payload } = event.data;
    
    switch (type) {
      case 'glyphPathBin':
        // Binary glyph paths are consumed by EdgeDocsCanvas message listener.
        // We ignore here to prevent "Unknown message type" logs.
        return;
      case 'glyphPathsWarmed':
        // Warm acks are consumed by EdgeDocsCanvas for telemetry; ignore here.
        return;
      case 'worker-log':
        // Forward worker logs to main console with special prefix
        const { message, data } = event.data;
        if (message) {
          DEBUG_ENABLED && console.log(`🔧 [WORKER] ${message}`, data !== undefined ? data : '');
        } else if (payload?.level && payload?.message) {
          // Legacy format support
          const prefix = '🔧 [WORKER] ';
          if (payload.level === 'error') {
            console.error(prefix + payload.message);
          } else if (payload.level === 'warn') {
            console.warn(prefix + payload.message);
          } else {
            DEBUG_ENABLED && console.log(prefix + payload.message);
          }
        }
        break;
        
      case 'font-warning':
        // ✅ NEW: Handle font warnings from worker
        if (payload?.missing && payload.missing.length > 0) {
          const missingFonts = joinArray(payload.missing);
          const availableFonts = joinArrayWithFallback(payload.available || []);
          console.error(`❌ [TypesetterWorkerClient] Worker missing fonts: ${missingFonts}`);
          DEBUG_ENABLED && console.log(`   Available in worker cache: ${availableFonts}`);
          
          // Notify via error handler
          const errorMsg = `Worker is missing required fonts: ${missingFonts}. Layout may be incorrect.`;
          this.options.onError?.(new Error(errorMsg));
        }
        break;
        
      case 'layout-response':
        if (payload) {
          LayoutResponseHandler.handleLayoutResponse(payload, this.pendingRequests, this.latestRequestId, { current: this.lastLayoutResult });
        }
        break;
        
      case 'layout-paginated-result':
      case 'layout-paginated-error':
        // ✅ Handled by direct event listeners in layoutDocumentPaginated()
        // (ignore in main handler to avoid "Unknown message type" warnings)
        break;
        
      case 'paginateFromCacheComplete':
        // ✅ Handled by direct event listeners in paginateFromCache()
        // (ignore in main handler to avoid "Unknown message type" warnings)
        break;
        
      case 'ready':
        // Already handled in initialize()
        break;
        
      case 'error':
        const errorMsg = payload?.error || 'Unknown worker error';
        console.error('[TypesetterWorkerClient] Worker error:', errorMsg);
        this.options.onError?.(new Error(errorMsg));
        break;
        
      // 🆕 Phase 1: Reactive message types
      case 'pageUpdate':
        this.handlePageUpdate(event.data as PageUpdateMessage);
        break;
        
      case 'caretMapUpdate':
        this.handleCaretMapUpdate(event.data as CaretMapUpdateMessage);
        break;
        
      case 'layoutProgress':
        this.handleLayoutProgress(event.data as LayoutProgressMessage);
        break;
        
      case 'layoutComplete':
        this.handleLayoutComplete(event.data as LayoutCompleteMessage);
        break;
        
      case 'requestParagraphText':
        this.handleRequestParagraphText(event.data as RequestParagraphTextMessage);
        break;
      
      // 🆕 Phase 3.7: Pure WASM response messages
      case 'initializeDocumentComplete':
        DocumentInitializationHandler.handleInitializeDocumentComplete(event.data, this.reactiveRequests, { current: this.documentInitialized });
        break;
        
      case 'applyEditDeltaComplete':
        // Handled by direct event listeners in applyEditDelta()
        break;

      case 'layoutAffectedParagraphsComplete':
        // Handled by direct event listeners in layoutAffectedParagraphs()
        break;
        
      default:
        DEBUG_ENABLED && console.warn('[TypesetterWorkerClient] Unknown message type:', type);
    }
  }
  
  // ============================================================================
  // 🆕 Phase 1: Reactive Message Handlers
  // ============================================================================
  
  /**
   * Handle pageUpdate message from worker
   */
  private handlePageUpdate(msg: PageUpdateMessage): void {
    DEBUG_ENABLED && console.log(`📥 [Reactive] pageUpdate (layoutId=${msg.layoutId}, page=${msg.page.pageIndex})`);
    
    // Check if layout was cancelled
    if (checkLayoutCancellation(this.cancelledLayoutIds, msg.layoutId, 'pageUpdate').shouldIgnore) {
      return;
    }
    
    // Call user callback if provided
    this.options.onPageUpdate?.(msg);
  }
  
  /**
   * Handle caretMapUpdate message from worker
   */
  private handleCaretMapUpdate(msg: CaretMapUpdateMessage): void {
    DEBUG_ENABLED && console.log(`📥 [Reactive] caretMapUpdate (layoutId=${msg.layoutId}, entries=${msg.caretMap.length})`);
    
    // Check if layout was cancelled
    if (checkLayoutCancellation(this.cancelledLayoutIds, msg.layoutId, 'caretMapUpdate').shouldIgnore) {
      return;
    }
    
    // Call user callback if provided
    this.options.onCaretMapUpdate?.(msg);
  }
  
  /**
   * Handle layoutProgress message from worker
   */
  private handleLayoutProgress(msg: LayoutProgressMessage): void {
    DEBUG_ENABLED && console.log(`📥 [Reactive] layoutProgress (layoutId=${msg.layoutId}, ${msg.progress.pagesProcessed}/${msg.progress.totalPages})`);
    
    // Check if layout was cancelled
    if (checkLayoutCancellation(this.cancelledLayoutIds, msg.layoutId, 'layoutProgress').shouldIgnore) {
      return;
    }
    
    // Call user callback if provided
    this.options.onLayoutProgress?.(msg);
  }
  
  /**
   * Handle layoutComplete message from worker
   */
  private handleLayoutComplete(msg: LayoutCompleteMessage): void {
    DEBUG_ENABLED && console.log(`📥 [Reactive] layoutComplete (layoutId=${msg.layoutId}, totalPages=${msg.totalPages})`);
    
    // Check if layout was cancelled
    if (checkLayoutCancellation(this.cancelledLayoutIds, msg.layoutId, 'layoutComplete').shouldIgnore) {
      removeCancelledStatus(this.cancelledLayoutIds, msg.layoutId);
      return;
    }
    
    // Resolve pending request
    // Update telemetry before resolving
    const telemetry = this.telemetryData.get(msg.layoutId);
    if (telemetry && telemetry.timing) {
      telemetry.timing.endMs = performance.now();
      telemetry.timing.durationMs = telemetry.timing.endMs - telemetry.timing.startMs;
      telemetry.work = {
        pagesComputed: msg.telemetry.pagesComputed,
        paragraphsCached: msg.telemetry.paragraphsCached,
        paragraphsReshaped: msg.telemetry.paragraphsReshaped,
        spilloverPages: 0 // Phase 4
      };
    }
    
    this.cleanupTelemetry();
    
    // Resolve the reactive request
    resolveReactiveRequest(this.reactiveRequests, msg.layoutId, msg);
    
    // Call user callback if provided
    this.options.onLayoutComplete?.(msg);
  }
  
  /**
   * Handle requestParagraphText message from worker (bidirectional)
   */
  private async handleRequestParagraphText(msg: RequestParagraphTextMessage): Promise<void> {
    DEBUG_ENABLED && console.log(`📥 [Reactive] requestParagraphText (layoutId=${msg.layoutId}, requestId=${msg.requestId}, paragraphs=${msg.paragraphIds.length})`);
    
    // Check if layout was cancelled
    if (checkLayoutCancellation(this.cancelledLayoutIds, msg.layoutId, 'requestParagraphText').shouldIgnore) {
      return;
    }
    
    // Call user callback if provided
    if (this.options.onRequestParagraphText) {
      try {
        const response = await this.options.onRequestParagraphText(msg);
        this.sendParagraphText(response);
      } catch (error) {
        console.error(`❌ [Reactive] Failed to handle requestParagraphText:`, error);
        // Send error response to worker
        // (Phase 2: will implement error response message)
      }
    } else {
      console.warn(`⚠️ [Reactive] No handler for requestParagraphText - worker won't receive paragraph data`);
    }
  }
  
  // ============================================================================
  // 🆕 Phase 3.7: Pure WASM Response Handlers
  // ============================================================================
  
  /**
   * Handle initializeDocumentComplete response
   */
  
  /**
   * Handle applyEditDeltaComplete response
   */
  private handleApplyEditDeltaComplete(msg: any): void {
    const { layoutId, affectedParagraphIds, deletedParagraphIds, newVersion, boundariesChanged, error } = msg;
    
    DEBUG_ENABLED && console.log(`📥 [Phase 3] applyEditDeltaComplete (layoutId=${layoutId}, affected=${affectedParagraphIds?.length}, deleted=${deletedParagraphIds?.length}, boundaries=${boundariesChanged})`);
    
    const request = this.reactiveRequests.get(layoutId);
    if (!request) {
      console.warn(`⚠️ [Phase 3] No pending request for layoutId ${layoutId}`);
      return;
    }
    
    // Clean up
    clearTimeout(request.timeout);
    this.reactiveRequests.delete(layoutId);
    
    const duration = calculateDuration(request.startTime, Date.now());
    DEBUG_ENABLED && console.log(`✅ [Phase 3] ${request.operation} completed in ${duration}ms`);
    
    // Soft-handle benign paragraph errors and OOB: resolve with empty affected IDs
    if (error && isSoftHandleableError(error)) {
      console.warn(`⚠️ [Phase 3] Soft-handling applyEditDelta error: ${error}`);
      request.resolve({
        affectedParagraphIds: [],
        deletedParagraphIds: [],
        newVersion,
        boundariesChanged: false
      });
      this.editInFlightCount = Math.max(0, this.editInFlightCount - 1);
      return;
    }

    if (error) {
      this.editInFlightCount = Math.max(0, this.editInFlightCount - 1);
      request.reject(new Error(error));
      return;
    }

    request.resolve({
      affectedParagraphIds,
      deletedParagraphIds: deletedParagraphIds || [],
      newVersion,
      boundariesChanged
    });
    this.editInFlightCount = Math.max(0, this.editInFlightCount - 1);
  }
  
  /**
   * Handle layoutAffectedParagraphsComplete response
   */
  private handleLayoutAffectedParagraphsComplete(msg: any): void {
    const { layoutId, snapshots, pages, caretMap, telemetry, error } = msg;
    
    DEBUG_ENABLED && console.log(`📥 [Phase 3] layoutAffectedParagraphsComplete (layoutId=${layoutId}, snapshots=${snapshots?.length}, pages=${pages?.length})`);
    DEBUG_ENABLED && console.log(`📥 [Phase 3] Message structure:`, { hasSnapshots: !!snapshots, hasPages: !!pages, hasCaretMap: !!caretMap });
    
    const request = this.reactiveRequests.get(layoutId);
    if (!request) {
      console.warn(`⚠️ [Phase 3] No pending request for layoutId ${layoutId}`);
      return;
    }
    
    // Clean up
    clearTimeout(request.timeout);
    this.reactiveRequests.delete(layoutId);
    
    const duration = calculateDuration(request.startTime, Date.now());
    const telemetryStr = stringifyWithTruncation(telemetry, 80);
    DEBUG_ENABLED && console.log(`✅ [Phase 3] ${request.operation} completed in ${duration}ms (telemetry: ${telemetryStr})`);
    
    if (error) {
      request.reject(new Error(error));
    } else {
      // Ensure arrays are never undefined
      request.resolve({
        snapshots: snapshots || [],
        pages: pages || [],
        caretMap: caretMap || [],
        telemetry
      });
    }
  }
  
  /**
   * Handle layout response
   */
  
  /**
   * Check if worker is ready
   */
  isWorkerReady(): boolean {
    return this.isReady;
  }
  
  /**
   * Get pending request count (for diagnostics)
   */
  getPendingCount(): number {
    return this.pendingRequests.size;
  }
  
  /**
   * 🆕 Get the last layout result (includes plan_hash)
   * Returns null if no layout has been performed yet
   */
  getLastLayoutResult(): ParagraphSnapshot | null {
    return this.lastLayoutResult;
  }
  
  // ============================================================================
  // 🆕 Phase 1: Reactive Message Protocol Methods
  // ============================================================================
  
  /**
   * Generate next layoutId
   */
  private getNextLayoutId(): LayoutId {
    return this.nextLayoutId++;
  }
  
  /**
   * Send init message to WASM (initialize document)
   * Phase 1: Foundation for reactive layout
   */
  async initDocument(message: Omit<InitMessage, 'type' | 'layoutId'>): Promise<void> {
    if (!this.worker || !this.isReady) {
      throw new Error(WORKER_NOT_INITIALIZED_ERROR);
    }
    
    const layoutId = this.getNextLayoutId();
    const fullMessage: InitMessage = {
      type: 'init',
      layoutId,
      ...message
    };
    
    DEBUG_ENABLED && console.log(`📤 [Reactive] Sending init message (layoutId=${layoutId})`);
    
    return new Promise((resolve, reject) => {
      const timeout = setTimeout(() => {
        this.reactiveRequests.delete(layoutId);
        reject(new Error('Init request timeout (10s)'));
      }, 10000);
      
      this.reactiveRequests.set(layoutId, {
        resolve,
        reject,
        timeout,
        startTime: performance.now(),
        operation: 'init'
      });
      
      this.telemetryData.set(layoutId, createTelemetryData(layoutId, 'init'));
      
      this.worker!.postMessage(fullMessage);
    });
  }
  
  /**
   * Send applyEdit message to WASM (incremental edit)
   * Phase 1: Foundation for reactive layout
   */
  async applyEdit(message: Omit<ApplyEditMessage, 'type' | 'layoutId'>): Promise<void> {
    if (!this.worker || !this.isReady) {
      throw new Error(WORKER_NOT_INITIALIZED_ERROR);
    }
    
    const layoutId = this.getNextLayoutId();
    const fullMessage: ApplyEditMessage = {
      type: 'applyEdit',
      layoutId,
      ...message
    };
    
    // Cancel previous layout if requested
    if (message.cancelPrevious && this.nextLayoutId > 1) {
      this.cancelLayout(layoutId - 1);
    }
    
    DEBUG_ENABLED && console.log(`📤 [Reactive] Sending applyEdit message (layoutId=${layoutId}, paragraphId=${message.edit.paragraphId})`);
    
    return new Promise((resolve, reject) => {
      const timeout = setTimeout(() => {
        this.reactiveRequests.delete(layoutId);
        reject(new Error('ApplyEdit request timeout (5s)'));
      }, 5000);
      
      this.reactiveRequests.set(layoutId, {
        resolve,
        reject,
        timeout,
        startTime: performance.now(),
        operation: 'applyEdit'
      });
      
      this.telemetryData.set(layoutId, createTelemetryData(layoutId, 'applyEdit'));
      
      this.worker!.postMessage(fullMessage);
    });
  }
  
  /**
   * Send requestPagination message to WASM
   * Phase 1: Foundation for reactive layout
   */
  async requestPagination(message: Omit<RequestPaginationMessage, 'type' | 'layoutId'>): Promise<void> {
    if (!this.worker || !this.isReady) {
      throw new Error(WORKER_NOT_INITIALIZED_ERROR);
    }
    
    const layoutId = this.getNextLayoutId();
    const fullMessage: RequestPaginationMessage = {
      type: 'requestPagination',
      layoutId,
      ...message
    };
    
    DEBUG_ENABLED && console.log(`📤 [Reactive] Sending requestPagination message (layoutId=${layoutId})`);
    
    return new Promise((resolve, reject) => {
      const timeout = setTimeout(() => {
        this.reactiveRequests.delete(layoutId);
        reject(new Error('RequestPagination timeout (30s)'));
      }, 30000);
      
      this.reactiveRequests.set(layoutId, {
        resolve,
        reject,
        timeout,
        startTime: performance.now(),
        operation: 'requestPagination'
      });
      
      this.worker!.postMessage(fullMessage);
    });
  }
  
  /**
   * Send updatePageSetup message to WASM
   * Phase 1: Foundation for reactive layout
   */
  async updatePageSetup(message: Omit<UpdatePageSetupMessage, 'type' | 'layoutId'>): Promise<void> {
    if (!this.worker || !this.isReady) {
      throw new Error(WORKER_NOT_INITIALIZED_ERROR);
    }
    
    const layoutId = this.getNextLayoutId();
    const fullMessage: UpdatePageSetupMessage = {
      type: 'updatePageSetup',
      layoutId,
      ...message
    };
    
    DEBUG_ENABLED && console.log(`📤 [Reactive] Sending updatePageSetup message (layoutId=${layoutId})`);
    
    return new Promise((resolve, reject) => {
      const timeout = setTimeout(() => {
        this.reactiveRequests.delete(layoutId);
        reject(new Error('UpdatePageSetup timeout (10s)'));
      }, 10000);
      
      this.reactiveRequests.set(layoutId, {
        resolve,
        reject,
        timeout,
        startTime: performance.now(),
        operation: 'updatePageSetup'
      });
      
      this.worker!.postMessage(fullMessage);
    });
  }
  
  /**
   * Cancel in-progress layout request
   * Phase 1: Cancellation support
   */
  cancelLayout(layoutId: LayoutId): void {
    if (!this.worker) return;
    
    DEBUG_ENABLED && console.log(`🚫 [Reactive] Cancelling layout (layoutId=${layoutId})`);
    
    // Mark as cancelled
    markLayoutAsCancelled(this.cancelledLayoutIds, layoutId);
    
    // Clear request if pending
    const request = this.reactiveRequests.get(layoutId);
    if (request) {
      clearTimeout(request.timeout);
      this.reactiveRequests.delete(layoutId);
      request.reject(new Error('Layout cancelled'));
    }
    
    // Send cancellation message to worker
    const cancelMessage: CancelLayoutMessage = {
      type: 'cancelLayout',
      layoutId
    };
    
    this.worker.postMessage(cancelMessage);
  }
  
  /**
   * Send paragraph text to WASM (response to requestParagraphText)
   * Phase 1: Bidirectional messaging
   */
  sendParagraphText(message: Omit<ReceiveParagraphTextMessage, 'type'>): void {
    if (!this.worker || !this.isReady) {
      DEBUG_ENABLED && console.warn('[Reactive] Cannot send paragraph text - worker not ready');
      return;
    }
    
    const fullMessage: ReceiveParagraphTextMessage = {
      type: 'receiveParagraphText',
      ...message
    };
    
    DEBUG_ENABLED && console.log(`📤 [Reactive] Sending receiveParagraphText (requestId=${message.requestId}, paragraphs=${message.paragraphs.length})`);
    
    this.worker.postMessage(fullMessage);
  }
  
  /**
   * Get telemetry data for a layout operation
   * Phase 1: Basic telemetry
   */
  getTelemetry(layoutId: LayoutId): Partial<LayoutTelemetry> | undefined {
    return this.telemetryData.get(layoutId);
  }
  
  /**
   * Clear old telemetry data (keep last 100)
   * Phase 1: Memory management
   */
  private cleanupTelemetry(): void {
    cleanupMap(this.telemetryData, 100);
  }
  
  /**
   * Terminate the worker
   */
  terminate(): void {
    if (this.worker) {
      DEBUG_ENABLED && console.log('🔌 [TypesetterWorkerClient] Terminating worker');
      
      // Reject all pending requests (legacy and reactive)
      cleanupAllRequests(this.pendingRequests, this.reactiveRequests, WORKER_TERMINATED_ERROR);
      resetWorkerState({
        cancelledLayoutIds: this.cancelledLayoutIds,
        telemetryData: this.telemetryData
      }, {
        clearCancelledLayouts: true,
        clearTelemetry: true
      });
      
      this.worker.terminate();
      this.worker = null;
      this.isReady = false;
    }
  }

  // ============================================================================
  // 🆕 Phase 4: Caret position tracking
  // ============================================================================

  private lastKnownCaretPage: number = 0;

  /**
   * Notify the client of the current caret position and resolve which page it's on.
   * Uses cached paragraph snapshots for O(log n) lookup.
   */
  notifyCaretPosition(graphemeIndex: number): {
    page: number;
    pageChanged: boolean;
    paragraphId: string | null;
    paragraphIndex: number;
    offsetInParagraph: number;
    yPosition?: number;
    heightIndexSize?: number;
    totalDocumentHeight?: number;
    binarySearchSteps?: number;
    crossedFormFeed?: boolean;
  } {
    const paragraphs = this.getDocumentParagraphs();
    let paragraphIndex = -1;
    let paragraphId: string | null = null;
    let offsetInParagraph = 0;

    // Binary search for the paragraph containing this grapheme
    for (let i = 0; i < paragraphs.length; i++) {
      if (graphemeIndex >= paragraphs[i].textStart && graphemeIndex <= paragraphs[i].textEnd) {
        paragraphIndex = i;
        paragraphId = paragraphs[i].id;
        offsetInParagraph = graphemeIndex - paragraphs[i].textStart;
        break;
      }
    }

    // Derive page from paragraphToPageMap if available
    let page = 0;
    if (paragraphId) {
      const pages = this.paragraphToPageMap.get(paragraphId);
      if (pages && pages.length > 0) {
        page = pages[0];
      }
    }

    const pageChanged = page !== this.lastKnownCaretPage;
    this.lastKnownCaretPage = page;

    return {
      page,
      pageChanged,
      paragraphId,
      paragraphIndex,
      offsetInParagraph,
    };
  }

  // ============================================================================
  // 🆕 Phase F: Paragraph metadata with formatting spans
  // ============================================================================

  /**
   * Get paragraph metadata including formatting information from cached snapshots.
   * Used by save-to-database operations that need full paragraph details.
   */
  async getParagraphMetadata(): Promise<{
    paragraphs: Array<{
      id: string;
      type: string;
      graphemeStart: number;
      graphemeEnd: number;
      version: number;
      lineSpacing?: number;
      fontSizePx?: number;
      fontFamily?: string;
      fontWeight?: number;
      fontStyle?: string;
      formattingSpans: Array<{ id: string; start: number; end: number }>;
    }>;
    version: number;
  }> {
    const baseParagraphs = this.getDocumentParagraphs();
    return {
      paragraphs: baseParagraphs.map(p => ({
        id: p.id,
        type: p.type,
        graphemeStart: p.textStart,
        graphemeEnd: p.textEnd,
        version: p.version,
        lineSpacing: 1.15,
        fontSizePx: 11,
        fontFamily: 'Arial',
        fontWeight: 400,
        fontStyle: 'normal',
        formattingSpans: [],
      })),
      version: this.currentDocumentVersion,
    };
  }
}

/**
 * React hook for TypesetterWorkerClient
 */
import { useEffect, useState, useRef } from 'react';

export function useTypesetterWorker(options?: TypesetterWorkerOptions) {
  const [isReady, setIsReady] = useState(false);
  const [error, setError] = useState<Error | null>(null);
  const clientRef = useRef<TypesetterWorkerClient | null>(null);
  
  useEffect(() => {
    // Create client
    const client = new TypesetterWorkerClient({
      onReady: () => {
        setIsReady(true);
        options?.onReady?.();
      },
      onError: (err) => {
        setError(err);
        options?.onError?.(err);
      }
    });
    
    clientRef.current = client;
    
    // Initialize
    client.initialize().catch((err) => {
      console.error('Failed to initialize typesetter worker:', err);
      setError(err);
    });
    
    // Cleanup
    return () => {
      client.terminate();
    };
  }, []); // Empty deps - only initialize once
  
  return {
    client: clientRef.current,
    isReady,
    error
  };
}

